"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.RichTextParser = void 0;
const rich_text_1 = require("../types/rich-text");
const splitColorPickerColorTokens_1 = require("../utils/splitColorPickerColorTokens");
const activitySpinnerMarkup_1 = require("../activities/activitySpinnerMarkup");
const sharedDataRichText_1 = require("../utils/sharedDataRichText");
class RichTextParser {
    /**
     * Parse a text description into rich text format
     * Supports markdown-like syntax: **bold**, *italic*, # headings, - lists, <location-image:id>, <portrait:id>
     * Note: Only *italic* syntax is supported (not _italic_) to avoid conflicts with variable names
     */
    static parseRichTextDescription(text) {
        if (!text || text.trim() === '') {
            return { elements: [] };
        }
        const normalizedText = this.collapseParagraphBreaksInsideScriptBlocks(this.normalizeConditionalTextStructure(text));
        const withHeadingParagraphBreaks = this.ensureHeadingParagraphBreaks(normalizedText);
        const withBlockElementParagraphBreaks = this.ensureBlockElementParagraphBreaks(withHeadingParagraphBreaks);
        // Split by double newlines to detect paragraphs
        const paragraphs = withBlockElementParagraphBreaks.split(/\n\s*\n/).filter(p => p.trim() !== '');
        // If no paragraphs found (single line), treat the entire text as one paragraph
        if (paragraphs.length === 0) {
            paragraphs.push(text.trim());
        }
        const elements = [];
        for (let i = 0; i < paragraphs.length; i++) {
            const paragraph = paragraphs[i];
            const trimmedParagraph = paragraph.trim();
            if (trimmedParagraph === '')
                continue;
            if (this.startsWithConditional(trimmedParagraph)) {
                const { element, endIndex } = this.parseConditionalBlock(paragraphs, i);
                elements.push(element);
                i = endIndex;
                continue;
            }
            if (this.isScrollAreaStart(trimmedParagraph)) {
                const { element, endIndex } = this.parseScrollAreaBlock(paragraphs, i);
                elements.push(element);
                i = endIndex;
                continue;
            }
            if (this.isSharedDataStart(trimmedParagraph)) {
                const { element, endIndex } = this.parseSharedDataBlock(paragraphs, i);
                elements.push(element);
                i = endIndex;
                continue;
            }
            const parsedElements = this.parseParagraph(trimmedParagraph);
            if (parsedElements) {
                if (Array.isArray(parsedElements)) {
                    elements.push(...parsedElements);
                }
                else {
                    elements.push(parsedElements);
                }
            }
        }
        return { elements };
    }
    static normalizeConditionalTag(paragraph) {
        const trimmed = paragraph.trim();
        return trimmed.replace(/"""\s*$/g, '').trim();
    }
    /**
     * Index of the closing {@code '''} for a Conditional {@code condition='''...'''} attribute.
     * The closing delimiter must be followed only by whitespace and {@code >}.
     */
    static findConditionalConditionCloseDelimiter(text, scriptStart) {
        for (let i = scriptStart; i <= text.length - 3; i++) {
            if (text[i] === "'" && text[i + 1] === "'" && text[i + 2] === "'") {
                if (/^\s*>/.test(text.slice(i + 3))) {
                    return i;
                }
            }
        }
        return null;
    }
    /**
     * When {@code text} begins with a Conditional open tag, returns the condition script and any
     * rich-text content that follows the {@code >} on the same paragraph.
     */
    static tryParseConditionalOpenAt(text) {
        const trimmed = text.trim();
        const openPrefix = trimmed.match(/^<Conditional\s+condition\s*=\s*'''/i);
        if (!openPrefix) {
            return null;
        }
        const scriptStart = openPrefix[0].length;
        const closeDelimiter = this.findConditionalConditionCloseDelimiter(trimmed, scriptStart);
        if (closeDelimiter === null) {
            return null;
        }
        const afterQuotes = closeDelimiter + 3;
        const gtMatch = trimmed.slice(afterQuotes).match(/^\s*>/);
        if (!gtMatch) {
            return null;
        }
        const openEnd = afterQuotes + gtMatch[0].length;
        return {
            conditionScript: trimmed.slice(scriptStart, closeDelimiter),
            afterOpenTag: trimmed.slice(openEnd).replace(/^\s+/, '')
        };
    }
    static isIncompleteConditionalOpen(text) {
        const trimmed = text.trim();
        if (!/^<Conditional\b/i.test(trimmed)) {
            return false;
        }
        return this.tryParseConditionalOpenAt(trimmed) === null;
    }
    /**
     * Joins consecutive paragraphs until a complete Conditional open tag (including multiline
     * {@code condition='''...'''}) is available.
     */
    static consumeConditionalOpenTag(paragraphs, startIndex) {
        let combined = '';
        for (let j = startIndex; j < paragraphs.length; j++) {
            if (j > startIndex) {
                combined += '\n\n';
            }
            combined += paragraphs[j].trim();
            const parsed = this.tryParseConditionalOpenAt(combined);
            if (parsed) {
                return {
                    conditionScript: parsed.conditionScript,
                    initialBranchContent: parsed.afterOpenTag,
                    contentStartIndex: j + 1
                };
            }
            if (j > startIndex && !this.isIncompleteConditionalOpen(combined)) {
                break;
            }
        }
        throw new Error('Conditional block must specify a condition using triple-quoted script syntax');
    }
    static wrapConditionalOpenTagsForParagraphSplit(text) {
        let result = text;
        let searchFrom = 0;
        while (searchFrom < result.length) {
            const idx = result.toLowerCase().indexOf('<conditional', searchFrom);
            if (idx === -1) {
                break;
            }
            const slice = result.slice(idx);
            const openPrefix = slice.match(/^<Conditional\s+condition\s*=\s*'''/i);
            if (!openPrefix) {
                searchFrom = idx + 1;
                continue;
            }
            const scriptStart = openPrefix[0].length;
            const closeDelimiter = this.findConditionalConditionCloseDelimiter(slice, scriptStart);
            if (closeDelimiter === null) {
                searchFrom = idx + 1;
                continue;
            }
            const afterQuotes = closeDelimiter + 3;
            const gtMatch = slice.slice(afterQuotes).match(/^\s*>/);
            if (!gtMatch) {
                searchFrom = idx + 1;
                continue;
            }
            const openEnd = afterQuotes + gtMatch[0].length;
            const openTag = slice.slice(0, openEnd);
            result = `${result.slice(0, idx)}\n${openTag}\n${result.slice(idx + openEnd)}`;
            searchFrom = idx + openTag.length + 2;
        }
        return result;
    }
    /**
     * Closing {@code '''} for a description script block (not a Conditional attribute).
     * Must be followed by whitespace, end of string, or a newline.
     */
    static findScriptBlockCloseDelimiter(text, scriptStart) {
        for (let i = scriptStart; i <= text.length - 3; i++) {
            if (text[i] === "'" && text[i + 1] === "'" && text[i + 2] === "'") {
                if (/^(\s|$)/.test(text.slice(i + 3))) {
                    return i;
                }
            }
        }
        return null;
    }
    /** True when {@code index} lies inside a {@code <Conditional condition='''...'''>} attribute value. */
    static isInsideConditionalConditionAttribute(text, index) {
        const marker = "condition='''";
        let search = 0;
        while (search < index) {
            const condIdx = text.indexOf(marker, search);
            if (condIdx === -1 || condIdx >= index) {
                break;
            }
            const scriptStart = condIdx + marker.length;
            const closeDelimiter = this.findConditionalConditionCloseDelimiter(text, scriptStart);
            if (closeDelimiter === null) {
                search = condIdx + 1;
                continue;
            }
            if (index >= condIdx && index < closeDelimiter + 3) {
                return true;
            }
            search = closeDelimiter + 3;
        }
        return false;
    }
    /**
     * True when {@code index} lies inside an unclosed {@code <option>} or {@code <Conditional>} start tag
     * (e.g. {@code target='''...'''} on options). Those {@code '''} delimiters are not description scripts.
     */
    static isInsideUnclosedAngleTag(text, index) {
        const before = text.slice(0, index);
        const lastLt = before.lastIndexOf('<');
        if (lastLt === -1) {
            return false;
        }
        const lastGt = before.lastIndexOf('>');
        if (lastGt >= lastLt) {
            return false;
        }
        const tagSlice = text.slice(lastLt + 1, lastLt + 30);
        return /^option\b/i.test(tagSlice) || /^Conditional\b/i.test(tagSlice);
    }
    /**
     * Blank lines inside multiline {@code '''script'''} blocks must not split paragraphs.
     * Collapses {@code \n\n} to {@code \n} only within description script blocks.
     */
    static collapseParagraphBreaksInsideScriptBlocks(text) {
        let result = text;
        let searchFrom = 0;
        while (searchFrom < result.length) {
            const idx = result.indexOf("'''", searchFrom);
            if (idx === -1) {
                break;
            }
            if (this.isInsideUnclosedAngleTag(result, idx)) {
                const closeGt = result.indexOf('>', idx);
                searchFrom = closeGt === -1 ? idx + 3 : closeGt + 1;
                continue;
            }
            if (this.isInsideConditionalConditionAttribute(result, idx)) {
                const marker = "condition='''";
                const condIdx = result.lastIndexOf(marker, idx);
                if (condIdx !== -1) {
                    const scriptStart = condIdx + marker.length;
                    const closeDelimiter = this.findConditionalConditionCloseDelimiter(result, scriptStart);
                    searchFrom = closeDelimiter === null ? idx + 3 : closeDelimiter + 3;
                }
                else {
                    searchFrom = idx + 3;
                }
                continue;
            }
            const scriptStart = idx + 3;
            const closeDelimiter = this.findScriptBlockCloseDelimiter(result, scriptStart);
            if (closeDelimiter === null) {
                searchFrom = idx + 3;
                continue;
            }
            const inner = result.slice(scriptStart, closeDelimiter);
            const collapsed = inner.replace(/\n\s*\n/g, '\n');
            result = result.slice(0, scriptStart) + collapsed + result.slice(closeDelimiter);
            searchFrom = scriptStart + collapsed.length + 3;
        }
        return result;
    }
    /**
     * Markdown headings must start a new paragraph. A single newline before {@code ## Title}
     * would otherwise keep the heading in the previous paragraph as literal {@code ##} text.
     */
    static ensureHeadingParagraphBreaks(text) {
        return text.replace(/\n(#{1,3}\s+)/g, '\n\n$1');
    }
    /**
     * Block elements ({@code <Shared-Data query="...">}) are only recognized when they start a
     * paragraph. A single newline after an inline {@code '''script'''} line keeps the block tag in
     * the same paragraph, so it renders as literal text. Only match real block opens (with required
     * attributes), not documentation mentions like {@code `<Shared-Data>`}.
     */
    static ensureBlockElementParagraphBreaks(text) {
        return text
            .replace(/([^\n])\n(<Shared-Data\b)/gi, '$1\n\n$2')
            .replace(/(<\/Shared-Data>)\n([^\n])/gi, '$1\n\n$2');
    }
    static normalizeConditionalTextStructure(text) {
        const normalizedLineEndings = text.replace(/\r\n/g, '\n');
        const withConditionalOpens = this.wrapConditionalOpenTagsForParagraphSplit(normalizedLineEndings);
        const separated = withConditionalOpens
            .replace(/<Else>/gi, '\n<Else>\n')
            .replace(/<\/Conditional>/gi, '\n</Conditional>\n')
            .replace(/<ScrollArea(?:\s+[^>]*)?>/gi, match => `\n${match}\n`)
            .replace(/<\/ScrollArea>/gi, match => `\n${match}\n`)
            .replace(/<Shared-Data[^>]*>/gi, match => `\n${match}\n`)
            .replace(/<\/Shared-Data>/gi, match => `\n${match}\n`);
        return separated.replace(/\n{3,}/g, '\n\n');
    }
    static startsWithConditional(paragraph) {
        const normalized = this.normalizeConditionalTag(paragraph);
        return /^<Conditional\b/i.test(normalized);
    }
    static isConditionalStart(paragraph) {
        return this.tryParseConditionalOpenAt(this.normalizeConditionalTag(paragraph)) !== null;
    }
    static isConditionalEnd(paragraph) {
        const normalized = this.normalizeConditionalTag(paragraph);
        return /^<\/Conditional>$/i.test(normalized);
    }
    static isElseParagraph(paragraph) {
        const normalized = this.normalizeConditionalTag(paragraph);
        return /^<Else>$/i.test(normalized);
    }
    /**
     * Split a paragraph that contains an inline {@code <Else>} line (single-newline separated from
     * preceding content). Without this, italic/script lines followed by {@code <Else>} on the next line
     * stay in one paragraph and the else branch is never recognized.
     */
    static splitParagraphAtElse(paragraph) {
        const lines = paragraph.split('\n');
        const beforeLines = [];
        const afterLines = [];
        let foundElse = false;
        for (const line of lines) {
            const trimmed = line.trim();
            if (!foundElse && this.isElseParagraph(trimmed)) {
                foundElse = true;
                continue;
            }
            if (!foundElse) {
                beforeLines.push(line);
            }
            else {
                afterLines.push(line);
            }
        }
        return {
            before: beforeLines.join('\n').trim(),
            hasElse: foundElse,
            after: afterLines.join('\n').trim()
        };
    }
    static parseConditionalBlock(paragraphs, startIndex) {
        const { conditionScript, initialBranchContent, contentStartIndex } = this.consumeConditionalOpenTag(paragraphs, startIndex);
        if (!conditionScript.trim()) {
            throw new Error('Conditional block condition cannot be empty');
        }
        const trueBranchParagraphs = [];
        const elseBranchParagraphs = [];
        let collectingElse = false;
        let depth = 0;
        if (initialBranchContent.trim().length > 0) {
            trueBranchParagraphs.push(initialBranchContent);
        }
        for (let i = contentStartIndex; i < paragraphs.length; i++) {
            const paragraph = paragraphs[i];
            const trimmed = paragraph.trim();
            if (this.isConditionalStart(trimmed)) {
                (collectingElse ? elseBranchParagraphs : trueBranchParagraphs).push(paragraph);
                depth++;
                continue;
            }
            if (this.isConditionalEnd(trimmed)) {
                if (depth === 0) {
                    const children = this.parseConditionalBranch(trueBranchParagraphs);
                    const elseChildren = collectingElse ? this.parseConditionalBranch(elseBranchParagraphs) : undefined;
                    return {
                        element: {
                            type: 'conditional',
                            condition: conditionScript,
                            children,
                            elseChildren
                        },
                        endIndex: i
                    };
                }
                (collectingElse ? elseBranchParagraphs : trueBranchParagraphs).push(paragraph);
                depth--;
                continue;
            }
            if (this.isElseParagraph(trimmed) && depth === 0) {
                if (collectingElse) {
                    throw new Error('Conditional block can only contain a single <Else> section');
                }
                collectingElse = true;
                continue;
            }
            if (depth === 0) {
                const splitAtElse = this.splitParagraphAtElse(paragraph);
                if (splitAtElse.hasElse) {
                    if (collectingElse) {
                        throw new Error('Conditional block can only contain a single <Else> section');
                    }
                    if (splitAtElse.before.length > 0) {
                        (collectingElse ? elseBranchParagraphs : trueBranchParagraphs).push(splitAtElse.before);
                    }
                    collectingElse = true;
                    if (splitAtElse.after.length > 0) {
                        elseBranchParagraphs.push(splitAtElse.after);
                    }
                    continue;
                }
            }
            (collectingElse ? elseBranchParagraphs : trueBranchParagraphs).push(paragraph);
        }
        throw new Error('Conditional block is missing a closing </Conditional> tag');
    }
    static isScrollAreaStart(paragraph) {
        return /^<ScrollArea(?:\s|>)/i.test(paragraph.trim());
    }
    static isScrollAreaEnd(paragraph) {
        return /^<\/ScrollArea>$/i.test(paragraph.trim());
    }
    static parseScrollAreaBlock(paragraphs, startIndex) {
        const startParagraph = paragraphs[startIndex].trim();
        const startMatch = startParagraph.match(/^<ScrollArea\s*([^>]*?)>([\s\S]*)?$/i);
        if (!startMatch) {
            throw new Error('Invalid ScrollArea opening tag');
        }
        const attrsText = (startMatch[1] || '').trim();
        let maxHeight;
        let scrollToBottom = false;
        if (attrsText) {
            const maxHeightMatch = attrsText.match(/maxHeight=([^\s>]+)/i);
            if (maxHeightMatch)
                maxHeight = maxHeightMatch[1].trim();
            scrollToBottom = /scrollToBottom(?:\s*=\s*(?:true|1))?/i.test(attrsText);
        }
        const contentParagraphs = [];
        const startRemainder = startMatch[2];
        if (startRemainder && startRemainder.trim().length > 0) {
            contentParagraphs.push(startRemainder.replace(/^\s+/, ''));
        }
        for (let i = startIndex + 1; i < paragraphs.length; i++) {
            const paragraph = paragraphs[i];
            const trimmed = paragraph.trim();
            if (this.isScrollAreaEnd(trimmed)) {
                const contentText = contentParagraphs.join('\n\n');
                const { elements } = this.parseRichTextDescription(contentText);
                return {
                    element: {
                        type: 'scroll-area',
                        maxHeight: maxHeight || undefined,
                        scrollToBottom: scrollToBottom || undefined,
                        content: elements
                    },
                    endIndex: i
                };
            }
            contentParagraphs.push(paragraph);
        }
        throw new Error('ScrollArea block is missing a closing </ScrollArea> tag');
    }
    static isSharedDataStart(paragraph) {
        const trimmed = paragraph.trim();
        if (!/^<Shared-Data\b/i.test(trimmed)) {
            return false;
        }
        const attrsMatch = trimmed.match(/^<Shared-Data\s*([^>]*?)>/i);
        return !!attrsMatch?.[1] && /query="/i.test(attrsMatch[1]);
    }
    static isSharedDataEnd(paragraph) {
        return /^<\/Shared-Data>$/i.test(paragraph.trim());
    }
    static parseSharedDataBlock(paragraphs, startIndex) {
        const startParagraph = paragraphs[startIndex].trim();
        const startMatch = startParagraph.match(/^<Shared-Data\s*([^>]*?)>([\s\S]*)?$/i);
        if (!startMatch) {
            throw new Error('Shared-Data block requires a query attribute, e.g. <Shared-Data query="poll_choice:total, poll_choice:count">');
        }
        const attrsText = (startMatch[1] || '').trim();
        const queryMatch = attrsText.match(/query="([^"]*)"/i);
        if (!queryMatch) {
            throw new Error('Shared-Data block requires a query attribute, e.g. <Shared-Data query="poll_choice:total, poll_choice:count">');
        }
        const query = queryMatch[1].trim();
        const queries = (0, sharedDataRichText_1.parseSharedDataQueryString)(query);
        const variantMatch = attrsText.match(/variant=([^\s>]+)/i);
        const variant = (0, sharedDataRichText_1.parseSharedDataVariant)(variantMatch?.[1]);
        const contentParagraphs = [];
        const startRemainder = startMatch[2];
        if (startRemainder && startRemainder.trim().length > 0) {
            contentParagraphs.push(startRemainder.replace(/^\s+/, ''));
        }
        for (let i = startIndex + 1; i < paragraphs.length; i++) {
            const paragraph = paragraphs[i];
            const trimmed = paragraph.trim();
            if (this.isSharedDataEnd(trimmed)) {
                const contentText = contentParagraphs.join('\n\n');
                const { elements } = this.parseRichTextDescription(contentText);
                return {
                    element: {
                        type: 'shared-data',
                        sharedDataAttributes: {
                            query,
                            queries,
                            ...(variant !== 'card' ? { variant } : {})
                        },
                        content: elements
                    },
                    endIndex: i
                };
            }
            contentParagraphs.push(paragraph);
        }
        throw new Error('Shared-Data block is missing a closing </Shared-Data> tag');
    }
    static parseConditionalBranch(paragraphs) {
        if (paragraphs.length === 0) {
            return [];
        }
        const branchText = paragraphs.join('\n\n');
        const { elements } = this.parseRichTextDescription(branchText);
        return elements;
    }
    static combineInlineOptionLines(lines) {
        const combined = [];
        let buffer = null;
        for (const line of lines) {
            const isOptionStart = line.startsWith('<option:');
            const hasOptionEnd = line.includes('</option>');
            if (buffer) {
                buffer.push(line);
                if (hasOptionEnd) {
                    combined.push(buffer.join('\n'));
                    buffer = null;
                }
                continue;
            }
            if (isOptionStart && !hasOptionEnd) {
                buffer = [line];
                continue;
            }
            combined.push(line);
        }
        if (buffer && buffer.length > 0) {
            combined.push(buffer.join('\n'));
        }
        return combined;
    }
    /**
     * Parse a single paragraph into a rich text element or array of elements
     */
    static parseParagraph(text) {
        const trimmed = text.trim();
        // Check for CharacterComment syntax (output of generateCharacterComments)
        const characterCommentElement = this.parseCharacterCommentElement(trimmed);
        if (characterCommentElement) {
            return characterCommentElement;
        }
        const feedbackCardElement = this.parseFeedbackCardElement(trimmed);
        if (feedbackCardElement) {
            return feedbackCardElement;
        }
        const analyticsTableRowElement = this.parseAnalyticsTableRowElement(trimmed);
        if (analyticsTableRowElement) {
            return analyticsTableRowElement;
        }
        // Check for ScheduleEvent syntax (output of generateScheduleBoard)
        const scheduleEventElement = this.parseScheduleEventElement(trimmed);
        if (scheduleEventElement) {
            return scheduleEventElement;
        }
        // Check for ScheduleEventTime syntax (output of generateScheduleEventTimeOptions)
        const scheduleEventTimeElement = this.parseScheduleEventTimeElement(trimmed);
        if (scheduleEventTimeElement) {
            return scheduleEventTimeElement;
        }
        // Check for JoinEventCard syntax (output of getUpcomingEventsAt)
        const joinEventCardElement = this.parseJoinEventCardElement(trimmed);
        if (joinEventCardElement) {
            return joinEventCardElement;
        }
        // Check for ScheduleEventAttendance syntax (output of getScheduleEventRequiredPortraitsWithPresence)
        const scheduleEventAttendanceElement = this.parseScheduleEventAttendanceElement(trimmed);
        if (scheduleEventAttendanceElement) {
            return scheduleEventAttendanceElement;
        }
        // Check for SyncOption syntax (output of getCharacterSynchronisationWaitOption)
        const syncOptionElement = this.parseSyncOptionElement(trimmed);
        if (syncOptionElement) {
            return syncOptionElement;
        }
        // Check for TimerDisplay syntax (output of displayTimer)
        const timerDisplayElement = this.parseTimerDisplayElement(trimmed);
        if (timerDisplayElement) {
            return timerDisplayElement;
        }
        // Check for TimerAudio syntax (output of displayTimerAudio)
        const timerAudioElement = this.parseTimerAudioElement(trimmed);
        if (timerAudioElement) {
            return timerAudioElement;
        }
        // Check for location-image syntax
        const locationImageMatch = trimmed.match(/^<location-image:((?:[a-z0-9]+(?:[/-][a-z0-9]+)*:[a-z0-9_]+|[a-z0-9_]+))>$/);
        if (locationImageMatch) {
            const imageId = locationImageMatch[1];
            if (this.isValidImageId(imageId)) {
                return {
                    type: 'image',
                    imageId: imageId,
                    imageType: 'location-image',
                    content: ''
                };
            }
        }
        // Check for portrait syntax with script and optional variant (e.g., <portrait:'''script''':thumbnail>)
        const portraitWithScriptMatchWithVariant = trimmed.match(/^<portrait:('''[^']+'''):(thumbnail|card|large)>$/);
        if (portraitWithScriptMatchWithVariant) {
            const scriptCode = portraitWithScriptMatchWithVariant[1].slice(3, -3);
            const variant = portraitWithScriptMatchWithVariant[2];
            if ((0, rich_text_1.isPortraitVariant)(variant)) {
                return {
                    type: 'image',
                    imageId: scriptCode,
                    imageType: 'portrait',
                    imageIdIsScript: true,
                    imageVariant: variant,
                    content: ''
                };
            }
        }
        // Check for portrait syntax with script without variant (defaults to card)
        const portraitWithScriptMatch = trimmed.match(/^<portrait:('''[^']+''')>$/);
        if (portraitWithScriptMatch) {
            const scriptCode = portraitWithScriptMatch[1].slice(3, -3);
            return {
                type: 'image',
                imageId: scriptCode,
                imageType: 'portrait',
                imageIdIsScript: true,
                content: ''
            };
        }
        // Check for portrait syntax with optional variant (e.g., <portrait:image-id:thumbnail>)
        const portraitMatchWithVariant = trimmed.match(/^<portrait:([a-z0-9_:-]+):(thumbnail|card|large)>$/);
        if (portraitMatchWithVariant) {
            const imageId = portraitMatchWithVariant[1];
            const variant = portraitMatchWithVariant[2];
            if (this.isValidImageId(imageId) && (0, rich_text_1.isPortraitVariant)(variant)) {
                return {
                    type: 'image',
                    imageId: imageId,
                    imageType: 'portrait',
                    imageVariant: variant,
                    content: ''
                };
            }
        }
        // Check for portrait syntax without variant (defaults to card)
        const portraitMatch = trimmed.match(/^<portrait:([a-z0-9_:-]+)>$/);
        if (portraitMatch) {
            const imageId = portraitMatch[1];
            if (this.isValidImageId(imageId)) {
                return {
                    type: 'image',
                    imageId: imageId,
                    imageType: 'portrait',
                    content: ''
                };
            }
        }
        // Check for title-image syntax with script
        const titleImageWithScriptMatch = trimmed.match(/^<title-image:('''[^']+''')>$/);
        if (titleImageWithScriptMatch) {
            const scriptCode = titleImageWithScriptMatch[1].slice(3, -3);
            return {
                type: 'image',
                imageId: scriptCode,
                imageType: 'title-image',
                imageIdIsScript: true,
                content: ''
            };
        }
        // Check for title-image syntax (simple or namespaced, including path adventure ids)
        const titleImageMatch = trimmed.match(/^<title-image:((?:[a-z0-9]+(?:[/-][a-z0-9]+)*:[a-z0-9_]+|[a-z0-9_]+))>$/);
        if (titleImageMatch) {
            const imageId = titleImageMatch[1];
            if (this.isValidImageId(imageId)) {
                return {
                    type: 'image',
                    imageId: imageId,
                    imageType: 'title-image',
                    content: ''
                };
            }
        }
        // Check for icon syntax with script and optional variant (e.g., <icon:'''script''':large>)
        const iconWithScriptMatchWithVariant = trimmed.match(/^<icon:('''[^']+'''):(small|large)>$/);
        if (iconWithScriptMatchWithVariant) {
            const scriptCode = iconWithScriptMatchWithVariant[1].slice(3, -3);
            const variant = iconWithScriptMatchWithVariant[2];
            return {
                type: 'image',
                imageId: scriptCode,
                imageType: 'icon',
                imageIdIsScript: true,
                imageVariant: variant,
                content: ''
            };
        }
        // Check for icon syntax with script without variant (defaults to small)
        const iconWithScriptMatch = trimmed.match(/^<icon:('''[^']+''')>$/);
        if (iconWithScriptMatch) {
            const scriptCode = iconWithScriptMatch[1].slice(3, -3);
            return {
                type: 'image',
                imageId: scriptCode,
                imageType: 'icon',
                imageIdIsScript: true,
                content: ''
            };
        }
        // Check for regular icon syntax with optional variant (e.g., <icon:image-id:large>)
        const iconMatchWithVariant = trimmed.match(/^<icon:([a-z0-9_:-]+):(small|large)>$/);
        if (iconMatchWithVariant) {
            const imageId = iconMatchWithVariant[1];
            const variant = iconMatchWithVariant[2];
            if (this.isValidImageId(imageId)) {
                return {
                    type: 'image',
                    imageId: imageId,
                    imageType: 'icon',
                    imageVariant: variant,
                    content: ''
                };
            }
        }
        // Check for regular icon syntax without variant (defaults to small)
        const iconMatch = trimmed.match(/^<icon:([a-z0-9_:-]+)>$/);
        if (iconMatch) {
            const imageId = iconMatch[1];
            if (this.isValidImageId(imageId)) {
                return {
                    type: 'image',
                    imageId: imageId,
                    imageType: 'icon',
                    content: ''
                };
            }
        }
        // Check for adventure syntax on its own paragraph (<adventure:id>)
        const adventureParagraphMatch = trimmed.match(/^<adventure:([a-z0-9]+(?:[/-][a-z0-9]+)*)>$/);
        if (adventureParagraphMatch) {
            const adventureId = adventureParagraphMatch[1];
            if (this.isValidAdventureId(adventureId)) {
                return {
                    type: 'adventure',
                    adventureId,
                    content: '',
                };
            }
        }
        // Check for Adventure-List syntax
        const adventureListMatch = trimmed.match(/^<Adventure-List(?:\s+tags=([a-zA-Z0-9_,-]+))?>$/);
        if (adventureListMatch) {
            const element = {
                type: 'adventure-list',
                content: ''
            };
            if (adventureListMatch[1]) {
                element.adventureListTags = adventureListMatch[1];
            }
            return element;
        }
        // Check for Divider syntax
        const dividerMatch = trimmed.match(/^<Divider>$/);
        if (dividerMatch) {
            return {
                type: 'divider'
            };
        }
        // Check for Active-Adventure-List syntax
        const activeAdventureListElement = RichTextParser.parseActiveAdventureListElement(trimmed);
        if (activeAdventureListElement) {
            return activeAdventureListElement;
        }
        // Check for active-adventure syntax
        const activeAdventureMatch = trimmed.match(/^<active-adventure:([a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})>$/i);
        if (activeAdventureMatch) {
            const sessionId = activeAdventureMatch[1].toLowerCase(); // Normalize to lowercase
            if (this.isValidSessionId(sessionId)) {
                return {
                    type: 'active-adventure',
                    sessionId: sessionId,
                    content: ''
                };
            }
        }
        // Check for Verification-Code-Input syntax
        const verificationCodeInputElement = this.parseVerificationCodeInputElement(trimmed);
        if (verificationCodeInputElement) {
            return verificationCodeInputElement;
        }
        // Check for User-Input syntax
        const userInputElement = this.parseUserInputElement(trimmed);
        if (userInputElement) {
            return userInputElement;
        }
        // Check for Spinner syntax
        const spinnerElement = this.parseSpinnerElement(trimmed);
        if (spinnerElement) {
            return spinnerElement;
        }
        // Standalone '''script''' paragraph (e.g. getActivitySpinnerMarkup on its own line)
        if (trimmed.startsWith("'''")) {
            const closeDelimiter = this.findScriptBlockCloseDelimiter(trimmed, 3);
            if (closeDelimiter !== null && trimmed.slice(closeDelimiter + 3).trim() === '') {
                const scriptCode = trimmed.slice(3, closeDelimiter).trim();
                if (scriptCode) {
                    return {
                        type: 'script',
                        scriptCode
                    };
                }
            }
        }
        // Check for Color-Picker syntax
        const colorPickerElement = this.parseColorPickerElement(trimmed);
        if (colorPickerElement) {
            return colorPickerElement;
        }
        // Check for Color-Sample syntax (read-only swatch from variable)
        const colorSampleElement = this.parseColorSampleElement(trimmed);
        if (colorSampleElement) {
            return colorSampleElement;
        }
        // Check for Code-Display syntax (read-only large code from variable)
        const codeDisplayElement = this.parseCodeDisplayElement(trimmed);
        if (codeDisplayElement) {
            return codeDisplayElement;
        }
        // Check for Time-Picker syntax
        const timePickerElement = this.parseTimePickerElement(trimmed);
        if (timePickerElement) {
            return timePickerElement;
        }
        // Check for Date-Picker syntax
        const datePickerElement = this.parseDatePickerElement(trimmed);
        if (datePickerElement) {
            return datePickerElement;
        }
        // Check for Location-With-Portraits syntax
        const locationWithPortraitsElement = this.parseLocationWithPortraitsElement(trimmed);
        if (locationWithPortraitsElement) {
            return locationWithPortraitsElement;
        }
        const activityListElement = this.parseActivityListElement(trimmed);
        if (activityListElement) {
            return activityListElement;
        }
        const entityListElement = this.parseEntityListElement(trimmed);
        if (entityListElement) {
            return entityListElement;
        }
        // Check for Divider syntax within inline content
        const dividerInlineMatch = trimmed.match(/^<Divider>$/);
        if (dividerInlineMatch) {
            return {
                type: 'divider'
            };
        }
        // Check for inventory-list syntax with script blocks
        const inventoryListWithScriptMatch = trimmed.match(/^<Inventory-List\s+(character|location)=('''[^']+''')(?:\s+selectionTarget=([a-zA-Z0-9_-]+))?>$/);
        if (inventoryListWithScriptMatch) {
            const inventoryType = inventoryListWithScriptMatch[1];
            const inventoryOwnerScript = inventoryListWithScriptMatch[2].slice(3, -3);
            const selectionTarget = inventoryListWithScriptMatch[3];
            return {
                type: 'inventory-list',
                inventoryType: inventoryType,
                inventoryOwner: inventoryOwnerScript,
                inventoryOwnerIsScript: true,
                selectionTarget: selectionTarget,
                content: ''
            };
        }
        // Check for inventory-list syntax (original format)
        // Note: Character names can contain spaces (e.g., "Lady Velvet")
        const inventoryListMatch = trimmed.match(/^<Inventory-List\s+(character|location)=([a-zA-Z0-9_\s-]+)(?:\s+selectionTarget=([a-zA-Z0-9_-]+))?>$/);
        if (inventoryListMatch) {
            const inventoryType = inventoryListMatch[1];
            const inventoryOwner = inventoryListMatch[2];
            const selectionTarget = inventoryListMatch[3];
            return {
                type: 'inventory-list',
                inventoryType: inventoryType,
                inventoryOwner: inventoryOwner,
                selectionTarget: selectionTarget,
                content: ''
            };
        }
        // Check for Friend-Requests syntax with selectionTarget
        const friendRequestsMatch = trimmed.match(/^<Friend-Requests(?:\s+selectionTarget=([a-zA-Z0-9_-]+))?>$/);
        if (friendRequestsMatch) {
            const selectionTarget = friendRequestsMatch[1];
            return {
                type: 'friend-requests',
                selectionTarget: selectionTarget,
                content: ''
            };
        }
        // Check for Friends-List syntax with optional selectionTarget and excludeInSession
        // Only match valid attributes (selectionTarget, excludeInSession) to reject wrong-case params
        const friendsListMatch = trimmed.match(/^<Friends-List(\s+(?:selectionTarget=[a-zA-Z0-9_-]+|excludeInSession=(?:true|false))(?:\s+(?:selectionTarget=[a-zA-Z0-9_-]+|excludeInSession=(?:true|false)))*)?\s*>$/);
        if (friendsListMatch) {
            const attrs = (friendsListMatch[1] || '').trim();
            const selectionTargetMatch = attrs.match(/selectionTarget=([a-zA-Z0-9_-]+)/);
            const excludeInSessionMatch = attrs.match(/excludeInSession=(true|false)/);
            return {
                type: 'friends-list',
                selectionTarget: selectionTargetMatch?.[1],
                excludeInSession: excludeInSessionMatch ? excludeInSessionMatch[1] === 'true' : undefined,
                content: ''
            };
        }
        // Check for Adventure-Invite-List syntax with selectionTarget
        const adventureInviteListMatch = trimmed.match(/^<Adventure-Invite-List(?:\s+selectionTarget=([a-zA-Z0-9_-]+))?>$/);
        if (adventureInviteListMatch) {
            const selectionTarget = adventureInviteListMatch[1];
            return {
                type: 'adventure-invite-list',
                selectionTarget: selectionTarget,
                content: ''
            };
        }
        // Check for Adventure-Invite-Status-List syntax with optional selectionTarget
        const adventureInviteStatusListMatch = trimmed.match(/^<Adventure-Invite-Status-List(?:\s+selectionTarget=([a-zA-Z0-9_-]+))?>$/);
        if (adventureInviteStatusListMatch) {
            const selectionTarget = adventureInviteStatusListMatch[1];
            return {
                type: 'adventure-invite-status-list',
                selectionTarget: selectionTarget,
                content: ''
            };
        }
        // Guest-Adventure-List: invites created from the current host session (sourceSessionId)
        const guestAdventureListElement = RichTextParser.parseGuestAdventureListElement(trimmed);
        if (guestAdventureListElement) {
            return guestAdventureListElement;
        }
        // Keyed-List: clickable rows from a JSON array stored in a string variable
        const keyedListMatch = trimmed.match(/^<Keyed-List\s+sourceVariable=([a-zA-Z0-9_]+)(?:\s+selectionTarget=([a-zA-Z0-9_-]+))?(?:\s+selectionVariable=([a-zA-Z0-9_]+))?\s*\/?>$/);
        if (keyedListMatch) {
            return {
                type: 'keyed-list',
                keyedListSourceVariable: keyedListMatch[1],
                selectionTarget: keyedListMatch[2],
                keyedListSelectionVariable: keyedListMatch[3] ?? 'selected_id',
                content: ''
            };
        }
        // Check if this paragraph contains portrait, location-image, or icon tags on separate lines
        const rawParagraphLines = trimmed.split('\n').map(line => line.trim()).filter(line => line);
        const paragraphLines = this.combineInlineOptionLines(rawParagraphLines);
        if (paragraphLines.length > 1) {
            const elements = [];
            let currentTextLines = [];
            // Check if the first line is a heading
            const firstLine = paragraphLines[0];
            const firstLineHeadingMatch = firstLine.match(/^(#{1,3})\s+(.+)$/);
            if (firstLineHeadingMatch) {
                const level = firstLineHeadingMatch[1].length;
                const content = firstLineHeadingMatch[2].trim();
                elements.push({
                    type: 'heading',
                    level: Math.min(level, 3), // Cap at level 3
                    content: this.parseInlineFormatting(content)
                });
                // Process the remaining lines (skip the first line since it's a heading)
                // When inline options follow a heading directly, they should be separate elements
                for (let i = 1; i < paragraphLines.length; i++) {
                    const line = paragraphLines[i];
                    // Check if this line is an inline option - process it separately
                    const inlineOptionMatch = line.match(/^<option:([^>]+)>([\s\S]*?)<\/option>$/);
                    if (inlineOptionMatch) {
                        // Add any accumulated text first
                        this.addAccumulatedText(currentTextLines, elements);
                        currentTextLines = [];
                        // Process the inline option
                        const params = inlineOptionMatch[1];
                        const content = inlineOptionMatch[2];
                        const optionResult = this.parseOptionTag(params, content);
                        if (optionResult) {
                            elements.push(optionResult);
                        }
                    }
                    else {
                        // Process other line types normally
                        const processed = this.processLine(line, currentTextLines, elements);
                        if (processed) {
                            currentTextLines = processed;
                        }
                        else {
                            currentTextLines.push(line);
                        }
                    }
                }
                // Add any remaining text as a paragraph
                this.addAccumulatedText(currentTextLines, elements);
                // Return the elements if we found any images or heading
                if (elements.length > 0) {
                    return elements;
                }
            }
            else {
                // First line is not a heading, process all lines normally
                // Track if we're accumulating inline options
                let accumulatingInlineOptions = false;
                let inlineOptionLines = [];
                for (const line of paragraphLines) {
                    // Check if this line is an inline option
                    const inlineOptionMatch = line.match(/^<option:([^>]+)>([\s\S]*?)<\/option>$/);
                    if (inlineOptionMatch) {
                        // Start accumulating inline options
                        if (!accumulatingInlineOptions) {
                            // Add any previous accumulated text first
                            this.addAccumulatedText(currentTextLines, elements);
                            currentTextLines = [];
                            accumulatingInlineOptions = true;
                        }
                        inlineOptionLines.push(line);
                    }
                    else {
                        // Not an inline option line
                        if (accumulatingInlineOptions) {
                            // Finish accumulating inline options - group them in a paragraph
                            if (inlineOptionLines.length > 0) {
                                const paragraphText = inlineOptionLines.join('\n');
                                const paragraphElement = this.parseTextAsParagraph(paragraphText);
                                if (paragraphElement) {
                                    if (Array.isArray(paragraphElement)) {
                                        elements.push(...paragraphElement);
                                    }
                                    else {
                                        elements.push(paragraphElement);
                                    }
                                }
                                inlineOptionLines = [];
                            }
                            accumulatingInlineOptions = false;
                        }
                        // Process this line normally
                        const processed = this.processLine(line, currentTextLines, elements);
                        if (processed) {
                            currentTextLines = processed;
                        }
                        else {
                            currentTextLines.push(line);
                        }
                    }
                }
                // Handle any remaining inline options
                if (accumulatingInlineOptions && inlineOptionLines.length > 0) {
                    const paragraphText = inlineOptionLines.join('\n');
                    const paragraphElement = this.parseTextAsParagraph(paragraphText);
                    if (paragraphElement) {
                        if (Array.isArray(paragraphElement)) {
                            elements.push(...paragraphElement);
                        }
                        else {
                            elements.push(paragraphElement);
                        }
                    }
                }
                // Add any remaining text as a paragraph
                this.addAccumulatedText(currentTextLines, elements);
                // Return the elements if we found any images
                if (elements.length > 0) {
                    return elements;
                }
            }
        }
        // Check for headings
        const headingMatch = trimmed.match(/^(#{1,3})\s+(.+)$/);
        if (headingMatch) {
            const level = headingMatch[1].length;
            const content = headingMatch[2].trim();
            return {
                type: 'heading',
                level: Math.min(level, 3), // Cap at level 3
                content: this.parseInlineFormatting(content)
            };
        }
        // Check for lists
        if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
            return this.parseBulletList(text);
        }
        if (/^\d+\.\s/.test(trimmed)) {
            return this.parseNumberedList(text);
        }
        const scheduleEventLines = trimmed
            .split('\n')
            .map(line => line.trim())
            .filter(Boolean)
            .map(line => this.parseScheduleEventElement(line))
            .filter((el) => Boolean(el));
        if (scheduleEventLines.length > 0) {
            const nonEmptyLines = trimmed.split('\n').map(line => line.trim()).filter(Boolean);
            if (scheduleEventLines.length === nonEmptyLines.length) {
                return scheduleEventLines;
            }
        }
        const scheduleEventTimeLines = trimmed
            .split('\n')
            .map(line => line.trim())
            .filter(Boolean)
            .map(line => this.parseScheduleEventTimeElement(line))
            .filter((el) => Boolean(el));
        if (scheduleEventTimeLines.length > 0) {
            const nonEmptyLines = trimmed.split('\n').map(line => line.trim()).filter(Boolean);
            if (scheduleEventTimeLines.length === nonEmptyLines.length) {
                return scheduleEventTimeLines;
            }
        }
        const joinEventCardLines = trimmed
            .split('\n')
            .map(line => line.trim())
            .filter(Boolean)
            .map(line => this.parseJoinEventCardElement(line))
            .filter((el) => Boolean(el));
        if (joinEventCardLines.length > 0) {
            const nonEmptyLines = trimmed.split('\n').map(line => line.trim()).filter(Boolean);
            if (joinEventCardLines.length === nonEmptyLines.length) {
                return joinEventCardLines;
            }
        }
        // Check if this is a paragraph followed by a list
        const lines = trimmed.split('\n');
        if (lines.length > 1) {
            // Look for the first list line
            let listStartIndex = -1;
            for (let i = 0; i < lines.length; i++) {
                const line = lines[i].trim();
                if (line.startsWith('- ') || line.startsWith('* ') || /^\d+\.\s/.test(line)) {
                    listStartIndex = i;
                    break;
                }
            }
            if (listStartIndex > 0) {
                // Split into paragraph and list parts
                const paragraphLines = lines.slice(0, listStartIndex);
                const listLines = lines.slice(listStartIndex);
                const paragraphText = paragraphLines.join('\n').trim();
                const listText = listLines.join('\n').trim();
                // Return a combined element with paragraph and list
                const paragraphElement = {
                    type: 'paragraph',
                    content: this.parseInlineFormatting(paragraphText)
                };
                let listElement;
                if (listText.startsWith('- ') || listText.startsWith('* ')) {
                    listElement = this.parseBulletList(listText);
                }
                else {
                    listElement = this.parseNumberedList(listText);
                }
                return [paragraphElement, listElement];
            }
        }
        // Regular paragraph
        const inlineElements = this.parseInlineFormatting(trimmed);
        if (inlineElements.length > 0 && inlineElements.every(el => el.type === 'schedule-event' || el.type === 'schedule-event-time' || el.type === 'join-event-card' || el.type === 'schedule-event-attendance' || el.type === 'sync-option' || el.type === 'feedback-card' || el.type === 'analytics-table-row')) {
            return inlineElements;
        }
        return {
            type: 'paragraph',
            content: inlineElements
        };
    }
    /**
     * Process a single line, checking for image tags and other special elements
     * Returns the updated currentTextLines array if the line was processed, null if it was a regular text line
     */
    static processLine(line, currentTextLines, elements) {
        // Check for CharacterComment
        const characterCommentElement = this.parseCharacterCommentElement(line);
        if (characterCommentElement) {
            this.addAccumulatedText(currentTextLines, elements);
            elements.push(characterCommentElement);
            return [];
        }
        const feedbackCardElement = this.parseFeedbackCardElement(line);
        if (feedbackCardElement) {
            this.addAccumulatedText(currentTextLines, elements);
            elements.push(feedbackCardElement);
            return [];
        }
        const analyticsTableRowElement = this.parseAnalyticsTableRowElement(line);
        if (analyticsTableRowElement) {
            this.addAccumulatedText(currentTextLines, elements);
            elements.push(analyticsTableRowElement);
            return [];
        }
        // Check for ScheduleEvent
        const scheduleEventElement = this.parseScheduleEventElement(line);
        if (scheduleEventElement) {
            this.addAccumulatedText(currentTextLines, elements);
            elements.push(scheduleEventElement);
            return [];
        }
        // Check for ScheduleEventTime
        const scheduleEventTimeElement = this.parseScheduleEventTimeElement(line);
        if (scheduleEventTimeElement) {
            this.addAccumulatedText(currentTextLines, elements);
            elements.push(scheduleEventTimeElement);
            return [];
        }
        // Check for JoinEventCard
        const joinEventCardElement = this.parseJoinEventCardElement(line);
        if (joinEventCardElement) {
            this.addAccumulatedText(currentTextLines, elements);
            elements.push(joinEventCardElement);
            return [];
        }
        // Try to parse as an image element
        const imageElement = this.parseImageLine(line);
        if (imageElement) {
            this.addAccumulatedText(currentTextLines, elements);
            elements.push(imageElement);
            return []; // Clear text accumulator
        }
        // Check for User-Input syntax on its own line
        const trimmedLine = line.trim();
        // Standalone '''script''' lines (e.g. getActivitySpinnerMarkup) must be block-level like <Spinner />
        if (trimmedLine.startsWith("'''")) {
            const closeDelimiter = this.findScriptBlockCloseDelimiter(trimmedLine, 3);
            if (closeDelimiter !== null && trimmedLine.slice(closeDelimiter + 3).trim() === '') {
                const scriptCode = trimmedLine.slice(3, closeDelimiter).trim();
                if (scriptCode) {
                    this.addAccumulatedText(currentTextLines, elements);
                    elements.push({
                        type: 'script',
                        scriptCode
                    });
                    return [];
                }
            }
        }
        const userInputElement = this.parseUserInputElement(trimmedLine);
        if (userInputElement) {
            this.addAccumulatedText(currentTextLines, elements);
            elements.push(userInputElement);
            return [];
        }
        // Check for Spinner syntax on its own line
        const spinnerElement = this.parseSpinnerElement(trimmedLine);
        if (spinnerElement) {
            this.addAccumulatedText(currentTextLines, elements);
            elements.push(spinnerElement);
            return [];
        }
        // Check for Color-Picker syntax on its own line
        const colorPickerLineElement = this.parseColorPickerElement(trimmedLine);
        if (colorPickerLineElement) {
            this.addAccumulatedText(currentTextLines, elements);
            elements.push(colorPickerLineElement);
            return [];
        }
        // Check for Color-Sample syntax on its own line
        const colorSampleLineElement = this.parseColorSampleElement(trimmedLine);
        if (colorSampleLineElement) {
            this.addAccumulatedText(currentTextLines, elements);
            elements.push(colorSampleLineElement);
            return [];
        }
        // Check for Code-Display syntax on its own line
        const codeDisplayLineElement = this.parseCodeDisplayElement(trimmedLine);
        if (codeDisplayLineElement) {
            this.addAccumulatedText(currentTextLines, elements);
            elements.push(codeDisplayLineElement);
            return [];
        }
        // Check for Time-Picker syntax on its own line
        const timePickerElement = this.parseTimePickerElement(trimmedLine);
        if (timePickerElement) {
            this.addAccumulatedText(currentTextLines, elements);
            elements.push(timePickerElement);
            return [];
        }
        // Check for Date-Picker syntax on its own line
        const datePickerElement = this.parseDatePickerElement(trimmedLine);
        if (datePickerElement) {
            this.addAccumulatedText(currentTextLines, elements);
            elements.push(datePickerElement);
            return [];
        }
        // Check for Location-With-Portraits syntax on its own line
        const locationWithPortraitsElement = this.parseLocationWithPortraitsElement(trimmedLine);
        if (locationWithPortraitsElement) {
            this.addAccumulatedText(currentTextLines, elements);
            elements.push(locationWithPortraitsElement);
            return [];
        }
        // Check for adventure syntax on its own line (<adventure:id>)
        const adventureLineMatch = line.match(/^<adventure:([a-z0-9]+(?:[/-][a-z0-9]+)*)>$/);
        if (adventureLineMatch) {
            const adventureId = adventureLineMatch[1];
            if (this.isValidAdventureId(adventureId)) {
                this.addAccumulatedText(currentTextLines, elements);
                elements.push({
                    type: 'adventure',
                    adventureId,
                    content: '',
                });
                return [];
            }
        }
        // Check for Adventure-List syntax
        const adventureListMatch = line.match(/^<Adventure-List(?:\s+tags=([a-zA-Z0-9_,-]+))?>$/);
        if (adventureListMatch) {
            this.addAccumulatedText(currentTextLines, elements);
            const element = {
                type: 'adventure-list',
                content: ''
            };
            if (adventureListMatch[1]) {
                element.adventureListTags = adventureListMatch[1];
            }
            elements.push(element);
            return [];
        }
        // Check for Divider syntax
        const dividerMatch = line.match(/^<Divider>$/);
        if (dividerMatch) {
            this.addAccumulatedText(currentTextLines, elements);
            elements.push({
                type: 'divider'
            });
            return [];
        }
        // Check for Active-Adventure-List syntax
        const activeAdventureListElement = RichTextParser.parseActiveAdventureListElement(line);
        if (activeAdventureListElement) {
            this.addAccumulatedText(currentTextLines, elements);
            elements.push(activeAdventureListElement);
            return [];
        }
        const activityListElement = this.parseActivityListElement(line.trim());
        if (activityListElement) {
            this.addAccumulatedText(currentTextLines, elements);
            elements.push(activityListElement);
            return [];
        }
        const entityListElement = this.parseEntityListElement(line.trim());
        if (entityListElement) {
            this.addAccumulatedText(currentTextLines, elements);
            elements.push(entityListElement);
            return [];
        }
        // Check for active-adventure syntax
        const activeAdventureMatch = line.match(/^<active-adventure:([a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})>$/i);
        if (activeAdventureMatch) {
            const sessionId = activeAdventureMatch[1].toLowerCase(); // Normalize to lowercase
            if (this.isValidSessionId(sessionId)) {
                this.addAccumulatedText(currentTextLines, elements);
                elements.push({
                    type: 'active-adventure',
                    sessionId: sessionId,
                    content: ''
                });
                return [];
            }
        }
        // Check for inventory-list syntax with script
        const inventoryListWithScriptMatch = line.match(/^<Inventory-List\s+(character|location)=('''[^']+''')(?:\s+selectionTarget=([a-zA-Z0-9_-]+))?>$/);
        if (inventoryListWithScriptMatch) {
            const inventoryType = inventoryListWithScriptMatch[1];
            const inventoryOwnerScript = inventoryListWithScriptMatch[2].slice(3, -3);
            const selectionTarget = inventoryListWithScriptMatch[3];
            this.addAccumulatedText(currentTextLines, elements);
            elements.push({
                type: 'inventory-list',
                inventoryType: inventoryType,
                inventoryOwner: inventoryOwnerScript,
                inventoryOwnerIsScript: true,
                selectionTarget: selectionTarget,
                content: ''
            });
            return [];
        }
        // Check for inventory-list syntax
        const inventoryListMatch = line.match(/^<Inventory-List\s+(character|location)=([a-zA-Z0-9_\s-]+)(?:\s+selectionTarget=([a-zA-Z0-9_-]+))?>$/);
        if (inventoryListMatch) {
            const inventoryType = inventoryListMatch[1];
            const inventoryOwner = inventoryListMatch[2];
            const selectionTarget = inventoryListMatch[3];
            this.addAccumulatedText(currentTextLines, elements);
            elements.push({
                type: 'inventory-list',
                inventoryType: inventoryType,
                inventoryOwner: inventoryOwner,
                selectionTarget: selectionTarget,
                content: ''
            });
            return [];
        }
        // Check for Friend-Requests syntax with selectionTarget
        const friendRequestsMatch = line.match(/^<Friend-Requests(?:\s+selectionTarget=([a-zA-Z0-9_-]+))?>$/);
        if (friendRequestsMatch) {
            const selectionTarget = friendRequestsMatch[1];
            this.addAccumulatedText(currentTextLines, elements);
            elements.push({
                type: 'friend-requests',
                selectionTarget: selectionTarget,
                content: ''
            });
            return [];
        }
        // Check for Friends-List syntax with optional selectionTarget and excludeInSession
        // Only match valid attributes to reject wrong-case params
        const friendsListMatch = line.match(/^<Friends-List(\s+(?:selectionTarget=[a-zA-Z0-9_-]+|excludeInSession=(?:true|false))(?:\s+(?:selectionTarget=[a-zA-Z0-9_-]+|excludeInSession=(?:true|false)))*)?\s*>$/);
        if (friendsListMatch) {
            const attrs = (friendsListMatch[1] || '').trim();
            const selectionTargetMatch = attrs.match(/selectionTarget=([a-zA-Z0-9_-]+)/);
            const excludeInSessionMatch = attrs.match(/excludeInSession=(true|false)/);
            this.addAccumulatedText(currentTextLines, elements);
            elements.push({
                type: 'friends-list',
                selectionTarget: selectionTargetMatch?.[1],
                excludeInSession: excludeInSessionMatch ? excludeInSessionMatch[1] === 'true' : undefined,
                content: ''
            });
            return [];
        }
        // Check for Adventure-Invite-List syntax with selectionTarget
        const adventureInviteListMatch = line.match(/^<Adventure-Invite-List(?:\s+selectionTarget=([a-zA-Z0-9_-]+))?>$/);
        if (adventureInviteListMatch) {
            const selectionTarget = adventureInviteListMatch[1];
            this.addAccumulatedText(currentTextLines, elements);
            elements.push({
                type: 'adventure-invite-list',
                selectionTarget: selectionTarget,
                content: ''
            });
            return [];
        }
        // Check for Adventure-Invite-Status-List syntax with optional selectionTarget
        const adventureInviteStatusListMatch = line.match(/^<Adventure-Invite-Status-List(?:\s+selectionTarget=([a-zA-Z0-9_-]+))?>$/);
        if (adventureInviteStatusListMatch) {
            const selectionTarget = adventureInviteStatusListMatch[1];
            this.addAccumulatedText(currentTextLines, elements);
            elements.push({
                type: 'adventure-invite-status-list',
                selectionTarget: selectionTarget,
                content: ''
            });
            return [];
        }
        // Guest-Adventure-List: invites created from the current host session (sourceSessionId)
        const guestAdventureListElement = RichTextParser.parseGuestAdventureListElement(line);
        if (guestAdventureListElement) {
            this.addAccumulatedText(currentTextLines, elements);
            elements.push(guestAdventureListElement);
            return [];
        }
        // Keyed-List: clickable rows from a JSON array stored in a string variable
        const keyedListMatch = line.match(/^<Keyed-List\s+sourceVariable=([a-zA-Z0-9_]+)(?:\s+selectionTarget=([a-zA-Z0-9_-]+))?(?:\s+selectionVariable=([a-zA-Z0-9_]+))?\s*\/?>$/);
        if (keyedListMatch) {
            this.addAccumulatedText(currentTextLines, elements);
            elements.push({
                type: 'keyed-list',
                keyedListSourceVariable: keyedListMatch[1],
                selectionTarget: keyedListMatch[2],
                keyedListSelectionVariable: keyedListMatch[3] ?? 'selected_id',
                content: ''
            });
            return [];
        }
        // Check for inline options - they can appear on their own lines
        // When on separate lines in a multi-line paragraph, they become separate elements
        // When grouped together (with blank lines), they can be parsed as part of paragraph content
        const inlineOptionMatch = line.match(/^<option:([^>]+)>([\s\S]*?)<\/option>$/);
        if (inlineOptionMatch) {
            const params = inlineOptionMatch[1];
            const content = inlineOptionMatch[2];
            this.addAccumulatedText(currentTextLines, elements);
            const optionResult = this.parseOptionTag(params, content);
            if (optionResult) {
                elements.push(optionResult);
            }
            return []; // Clear text accumulator
        }
        // This is a regular text line, accumulate it
        return null;
    }
    /**
     * Parse a line for image tags (portrait, title-image, location-image, icon)
     * Returns the image element if found, null otherwise
     */
    static parseImageLine(line) {
        // Check for portrait with script and optional variant (e.g., <portrait:'''script''':thumbnail>)
        const portraitWithScriptMatchWithVariant = line.match(/^<portrait:('''[^']+'''):(thumbnail|card|large)>$/);
        if (portraitWithScriptMatchWithVariant) {
            const scriptCode = portraitWithScriptMatchWithVariant[1].slice(3, -3);
            const variant = portraitWithScriptMatchWithVariant[2];
            if ((0, rich_text_1.isPortraitVariant)(variant)) {
                return {
                    type: 'image',
                    imageId: scriptCode,
                    imageType: 'portrait',
                    imageIdIsScript: true,
                    imageVariant: variant,
                    content: ''
                };
            }
        }
        // Check for portrait with script without variant (defaults to card)
        const portraitWithScriptMatch = line.match(/^<portrait:('''[^']+''')>$/);
        if (portraitWithScriptMatch) {
            const scriptCode = portraitWithScriptMatch[1].slice(3, -3);
            return {
                type: 'image',
                imageId: scriptCode,
                imageType: 'portrait',
                imageIdIsScript: true,
                content: ''
            };
        }
        // Check for portrait with optional variant (e.g., <portrait:image-id:thumbnail>)
        const portraitMatchWithVariant = line.match(/^<portrait:([a-z0-9_:-]+):(thumbnail|card|large)>$/);
        if (portraitMatchWithVariant) {
            const imageId = portraitMatchWithVariant[1];
            const variant = portraitMatchWithVariant[2];
            if (this.isValidImageId(imageId) && (0, rich_text_1.isPortraitVariant)(variant)) {
                return {
                    type: 'image',
                    imageId: imageId,
                    imageType: 'portrait',
                    imageVariant: variant,
                    content: ''
                };
            }
        }
        // Check for portrait without variant (defaults to card)
        const portraitMatch = line.match(/^<portrait:([a-z0-9_:-]+)>$/);
        if (portraitMatch) {
            const imageId = portraitMatch[1];
            if (this.isValidImageId(imageId)) {
                return {
                    type: 'image',
                    imageId: imageId,
                    imageType: 'portrait',
                    content: ''
                };
            }
        }
        // Check for title-image with script
        const titleImageWithScriptMatch = line.match(/^<title-image:('''[^']+''')>$/);
        if (titleImageWithScriptMatch) {
            const scriptCode = titleImageWithScriptMatch[1].slice(3, -3);
            return {
                type: 'image',
                imageId: scriptCode,
                imageType: 'title-image',
                imageIdIsScript: true,
                content: ''
            };
        }
        // Check for title-image (simple or namespaced, including path adventure ids)
        const titleImageMatch = line.match(/^<title-image:((?:[a-z0-9]+(?:[/-][a-z0-9]+)*:[a-z0-9_]+|[a-z0-9_]+))>$/);
        if (titleImageMatch) {
            const imageId = titleImageMatch[1];
            if (this.isValidImageId(imageId)) {
                return {
                    type: 'image',
                    imageId: imageId,
                    imageType: 'title-image',
                    content: ''
                };
            }
        }
        // Check for location-image
        const locationImageMatch = line.match(/^<location-image:((?:[a-z0-9]+(?:[/-][a-z0-9]+)*:[a-z0-9_]+|[a-z0-9_]+))>$/);
        if (locationImageMatch) {
            const imageId = locationImageMatch[1];
            if (this.isValidImageId(imageId)) {
                return {
                    type: 'image',
                    imageId: imageId,
                    imageType: 'location-image',
                    content: ''
                };
            }
        }
        // Check for icon with script and variant
        const iconWithScriptMatchWithVariant = line.match(/^<icon:('''[^']+'''):(small|large)>$/);
        if (iconWithScriptMatchWithVariant) {
            const scriptCode = iconWithScriptMatchWithVariant[1].slice(3, -3);
            const variant = iconWithScriptMatchWithVariant[2];
            return {
                type: 'image',
                imageId: scriptCode,
                imageType: 'icon',
                imageIdIsScript: true,
                imageVariant: variant,
                content: ''
            };
        }
        // Check for icon with script (no variant)
        const iconWithScriptMatch = line.match(/^<icon:('''[^']+''')>$/);
        if (iconWithScriptMatch) {
            const scriptCode = iconWithScriptMatch[1].slice(3, -3);
            return {
                type: 'image',
                imageId: scriptCode,
                imageType: 'icon',
                imageIdIsScript: true,
                content: ''
            };
        }
        // Check for icon with variant
        const iconMatchWithVariant = line.match(/^<icon:([a-z0-9_:-]+):(small|large)>$/);
        if (iconMatchWithVariant) {
            const imageId = iconMatchWithVariant[1];
            const variant = iconMatchWithVariant[2];
            if (this.isValidImageId(imageId)) {
                return {
                    type: 'image',
                    imageId: imageId,
                    imageType: 'icon',
                    imageVariant: variant,
                    content: ''
                };
            }
        }
        // Check for icon (no variant)
        const iconMatch = line.match(/^<icon:([a-z0-9_:-]+)>$/);
        if (iconMatch) {
            const imageId = iconMatch[1];
            if (this.isValidImageId(imageId)) {
                return {
                    type: 'image',
                    imageId: imageId,
                    imageType: 'icon',
                    content: ''
                };
            }
        }
        return null;
    }
    /**
     * Add accumulated text lines as a paragraph to elements if any exist
     */
    static addAccumulatedText(currentTextLines, elements) {
        if (currentTextLines.length > 0) {
            const paragraphText = currentTextLines.join('\n');
            const paragraphElement = this.parseTextAsParagraph(paragraphText);
            if (paragraphElement) {
                if (Array.isArray(paragraphElement)) {
                    elements.push(...paragraphElement);
                }
                else {
                    elements.push(paragraphElement);
                }
            }
        }
    }
    /**
     * Parse bullet list
     */
    static parseBulletList(text) {
        const lines = text.split('\n').map(line => line.trim()).filter(line => line);
        const items = [];
        for (const line of lines) {
            if (line.startsWith('- ') || line.startsWith('* ')) {
                const content = line.substring(2).trim();
                items.push({
                    type: 'text',
                    content: this.parseInlineFormatting(content)
                });
            }
        }
        return {
            type: 'list',
            listType: 'bullet',
            content: items
        };
    }
    /**
     * Parse numbered list
     */
    static parseNumberedList(text) {
        const lines = text.split('\n').map(line => line.trim()).filter(line => line);
        const items = [];
        for (const line of lines) {
            const match = line.match(/^(\d+)\.\s(.+)$/);
            if (match) {
                const content = match[2].trim();
                items.push({
                    type: 'text',
                    content: this.parseInlineFormatting(content)
                });
            }
        }
        return {
            type: 'list',
            listType: 'numbered',
            content: items
        };
    }
    /**
     * Parse inline formatting (bold, italic, icons, inline options, script blocks)
     */
    static parseInlineFormatting(text) {
        const elements = [];
        let currentText = text ?? '';
        let position = 0;
        while (position < currentText.length) {
            // Look for script block syntax '''script_code'''
            // Check if we're at the start of a script block
            const remainingText = currentText.substring(position);
            if (remainingText.startsWith("'''")) {
                // Found script start at current position
                const scriptEnd = currentText.indexOf("'''", position + 3);
                if (scriptEnd !== -1) {
                    const scriptCode = currentText.substring(position + 3, scriptEnd);
                    // Add any text before the script
                    if (position > 0) {
                        const beforeText = currentText.substring(0, position);
                        if (beforeText.trim()) {
                            elements.push({ type: 'text', content: beforeText });
                        }
                    }
                    // Add the script element
                    elements.push({
                        type: 'script',
                        scriptCode: scriptCode
                    });
                    // Update position and currentText
                    const scriptEndPos = scriptEnd + 3;
                    currentText = currentText.substring(scriptEndPos);
                    position = 0;
                    continue;
                }
            }
            // Look for inline option syntax <option:target=scene_id:effects='''script'''>content</option>
            const optionMatch = currentText.substring(position).match(/^<option:([^>]+)>(.*?)<\/option>/s);
            if (optionMatch) {
                const params = optionMatch[1];
                const content = optionMatch[2];
                // Parse option parameters
                const optionResult = this.parseOptionTag(params, content);
                if (optionResult) {
                    // Add any text before the option
                    if (position > 0) {
                        const beforeText = currentText.substring(0, position);
                        if (beforeText.trim()) {
                            elements.push({ type: 'text', content: beforeText });
                        }
                    }
                    // Add the inline option
                    elements.push(optionResult);
                    // Update position and currentText
                    position += optionMatch[0].length;
                    currentText = currentText.substring(position);
                    position = 0;
                    continue;
                }
            }
            // Look for user input syntax <User-Input ... />
            const userInputMatch = currentText.substring(position).match(/^<User-Input\b[^>]*\/>/);
            if (userInputMatch) {
                const userInputElement = this.parseUserInputElement(userInputMatch[0].trim());
                if (userInputElement) {
                    if (position > 0) {
                        const beforeText = currentText.substring(0, position);
                        if (beforeText.trim()) {
                            elements.push({ type: 'text', content: beforeText });
                        }
                    }
                    elements.push(userInputElement);
                    position += userInputMatch[0].length;
                    currentText = currentText.substring(position);
                    position = 0;
                    continue;
                }
            }
            // Look for verification code input syntax <Verification-Code-Input ... />
            const verificationCodeInputMatch = currentText.substring(position).match(/^<Verification-Code-Input\b[^>]*\/>/);
            if (verificationCodeInputMatch) {
                const verificationCodeInputElement = this.parseVerificationCodeInputElement(verificationCodeInputMatch[0].trim());
                if (verificationCodeInputElement) {
                    if (position > 0) {
                        const beforeText = currentText.substring(0, position);
                        if (beforeText.trim()) {
                            elements.push({ type: 'text', content: beforeText });
                        }
                    }
                    elements.push(verificationCodeInputElement);
                    position += verificationCodeInputMatch[0].length;
                    currentText = currentText.substring(position);
                    position = 0;
                    continue;
                }
            }
            // Look for Spinner syntax <Spinner ... />
            const spinnerMatch = currentText.substring(position).match(/^<Spinner\b[^>]*\/>/);
            if (spinnerMatch) {
                const spinnerElement = this.parseSpinnerElement(spinnerMatch[0].trim());
                if (spinnerElement) {
                    if (position > 0) {
                        const beforeText = currentText.substring(0, position);
                        if (beforeText.trim()) {
                            elements.push({ type: 'text', content: beforeText });
                        }
                    }
                    elements.push(spinnerElement);
                    position += spinnerMatch[0].length;
                    currentText = currentText.substring(position);
                    position = 0;
                    continue;
                }
            }
            // Look for Color-Picker syntax <Color-Picker ... />
            const colorPickerMatch = currentText.substring(position).match(/^<Color-Picker\b[^>]*\/>/);
            if (colorPickerMatch) {
                const colorPickerInlineElement = this.parseColorPickerElement(colorPickerMatch[0].trim());
                if (colorPickerInlineElement) {
                    if (position > 0) {
                        const beforeText = currentText.substring(0, position);
                        if (beforeText.trim()) {
                            elements.push({ type: 'text', content: beforeText });
                        }
                    }
                    elements.push(colorPickerInlineElement);
                    position += colorPickerMatch[0].length;
                    currentText = currentText.substring(position);
                    position = 0;
                    continue;
                }
            }
            // Look for Color-Sample syntax <Color-Sample ... />
            const colorSampleMatch = currentText.substring(position).match(/^<Color-Sample\b[^>]*\/>/);
            if (colorSampleMatch) {
                const colorSampleInlineElement = this.parseColorSampleElement(colorSampleMatch[0].trim());
                if (colorSampleInlineElement) {
                    if (position > 0) {
                        const beforeText = currentText.substring(0, position);
                        if (beforeText.trim()) {
                            elements.push({ type: 'text', content: beforeText });
                        }
                    }
                    elements.push(colorSampleInlineElement);
                    position += colorSampleMatch[0].length;
                    currentText = currentText.substring(position);
                    position = 0;
                    continue;
                }
            }
            // Look for Code-Display syntax <Code-Display ... />
            const codeDisplayMatch = currentText.substring(position).match(/^<Code-Display\b[^>]*\/>/);
            if (codeDisplayMatch) {
                const codeDisplayInlineElement = this.parseCodeDisplayElement(codeDisplayMatch[0].trim());
                if (codeDisplayInlineElement) {
                    if (position > 0) {
                        const beforeText = currentText.substring(0, position);
                        if (beforeText.trim()) {
                            elements.push({ type: 'text', content: beforeText });
                        }
                    }
                    elements.push(codeDisplayInlineElement);
                    position += codeDisplayMatch[0].length;
                    currentText = currentText.substring(position);
                    position = 0;
                    continue;
                }
            }
            // Look for Time-Picker syntax <Time-Picker ... />
            const timePickerMatch = currentText.substring(position).match(/^<Time-Picker\b[^>]*\/>/);
            if (timePickerMatch) {
                const timePickerElement = this.parseTimePickerElement(timePickerMatch[0].trim());
                if (timePickerElement) {
                    if (position > 0) {
                        const beforeText = currentText.substring(0, position);
                        if (beforeText.trim()) {
                            elements.push({ type: 'text', content: beforeText });
                        }
                    }
                    elements.push(timePickerElement);
                    position += timePickerMatch[0].length;
                    currentText = currentText.substring(position);
                    position = 0;
                    continue;
                }
            }
            // Look for Date-Picker syntax <Date-Picker ... />
            const datePickerMatch = currentText.substring(position).match(/^<Date-Picker\b[^>]*\/>/);
            if (datePickerMatch) {
                const datePickerElement = this.parseDatePickerElement(datePickerMatch[0].trim());
                if (datePickerElement) {
                    if (position > 0) {
                        const beforeText = currentText.substring(0, position);
                        if (beforeText.trim()) {
                            elements.push({ type: 'text', content: beforeText });
                        }
                    }
                    elements.push(datePickerElement);
                    position += datePickerMatch[0].length;
                    currentText = currentText.substring(position);
                    position = 0;
                    continue;
                }
            }
            // Look for Location-With-Portraits syntax <Location-With-Portraits ... />
            const locationWithPortraitsMatch = currentText.substring(position).match(/^<Location-With-Portraits\b[^>]*\/>/i);
            if (locationWithPortraitsMatch) {
                const locationWithPortraitsElement = this.parseLocationWithPortraitsElement(locationWithPortraitsMatch[0].trim());
                if (locationWithPortraitsElement) {
                    if (position > 0) {
                        const beforeText = currentText.substring(0, position);
                        if (beforeText.trim()) {
                            elements.push({ type: 'text', content: beforeText });
                        }
                    }
                    elements.push(locationWithPortraitsElement);
                    position += locationWithPortraitsMatch[0].length;
                    currentText = currentText.substring(position);
                    position = 0;
                    continue;
                }
            }
            // Look for CharacterComment syntax <CharacterComment character="..." portrait="..." text="..." timestamp="...">
            const characterCommentMatch = currentText.substring(position).match(/^<CharacterComment\s[^>]*>/);
            if (characterCommentMatch) {
                const tag = characterCommentMatch[0];
                const characterCommentElement = this.parseCharacterCommentElement(tag);
                if (characterCommentElement) {
                    if (position > 0) {
                        const beforeText = currentText.substring(0, position);
                        if (beforeText.trim()) {
                            elements.push({ type: 'text', content: beforeText });
                        }
                    }
                    elements.push(characterCommentElement);
                    position += characterCommentMatch[0].length;
                    currentText = currentText.substring(position);
                    position = 0;
                    continue;
                }
            }
            const feedbackCardMatch = currentText.substring(position).match(/^<FeedbackCard\s[^>]*>/i);
            if (feedbackCardMatch) {
                const tag = feedbackCardMatch[0];
                const feedbackCardElement = this.parseFeedbackCardElement(tag);
                if (feedbackCardElement) {
                    if (position > 0) {
                        const beforeText = currentText.substring(0, position);
                        if (beforeText.trim()) {
                            elements.push({ type: 'text', content: beforeText });
                        }
                    }
                    elements.push(feedbackCardElement);
                    position += feedbackCardMatch[0].length;
                    currentText = currentText.substring(position);
                    position = 0;
                    continue;
                }
            }
            const analyticsTableRowMatch = currentText.substring(position).match(/^<AnalyticsTableRow\s[^>]*>/i);
            if (analyticsTableRowMatch) {
                const tag = analyticsTableRowMatch[0];
                const analyticsTableRowElement = this.parseAnalyticsTableRowElement(tag);
                if (analyticsTableRowElement) {
                    if (position > 0) {
                        const beforeText = currentText.substring(0, position);
                        if (beforeText.trim()) {
                            elements.push({ type: 'text', content: beforeText });
                        }
                    }
                    elements.push(analyticsTableRowElement);
                    position += analyticsTableRowMatch[0].length;
                    currentText = currentText.substring(position);
                    position = 0;
                    continue;
                }
            }
            // Look for ScheduleEvent syntax <ScheduleEvent ...>
            const scheduleEventMatch = currentText.substring(position).match(/^<ScheduleEvent\s[^>]*\/?>/i);
            if (scheduleEventMatch) {
                const tag = scheduleEventMatch[0];
                const scheduleEventElement = this.parseScheduleEventElement(tag);
                if (scheduleEventElement) {
                    if (position > 0) {
                        const beforeText = currentText.substring(0, position);
                        if (beforeText.trim()) {
                            elements.push({ type: 'text', content: beforeText });
                        }
                    }
                    elements.push(scheduleEventElement);
                    position += scheduleEventMatch[0].length;
                    currentText = currentText.substring(position);
                    position = 0;
                    continue;
                }
            }
            // Look for ScheduleEventTime syntax <ScheduleEventTime ...>
            const scheduleEventTimeMatch = currentText.substring(position).match(/^<ScheduleEventTime\s[^>]*\/?>/i);
            if (scheduleEventTimeMatch) {
                const tag = scheduleEventTimeMatch[0];
                const scheduleEventTimeElement = this.parseScheduleEventTimeElement(tag);
                if (scheduleEventTimeElement) {
                    if (position > 0) {
                        const beforeText = currentText.substring(0, position);
                        if (beforeText.trim()) {
                            elements.push({ type: 'text', content: beforeText });
                        }
                    }
                    elements.push(scheduleEventTimeElement);
                    position += scheduleEventTimeMatch[0].length;
                    currentText = currentText.substring(position);
                    position = 0;
                    continue;
                }
            }
            // Look for JoinEventCard syntax <JoinEventCard ...>
            const joinEventCardMatch = currentText.substring(position).match(/^<JoinEventCard\s[^>]*\/?>/i);
            if (joinEventCardMatch) {
                const tag = joinEventCardMatch[0];
                const joinEventCardElement = this.parseJoinEventCardElement(tag);
                if (joinEventCardElement) {
                    if (position > 0) {
                        const beforeText = currentText.substring(0, position);
                        if (beforeText.trim()) {
                            elements.push({ type: 'text', content: beforeText });
                        }
                    }
                    elements.push(joinEventCardElement);
                    position += joinEventCardMatch[0].length;
                    currentText = currentText.substring(position);
                    position = 0;
                    continue;
                }
            }
            // Look for ScheduleEventAttendance syntax <ScheduleEventAttendance ...>
            const scheduleEventAttendanceMatch = currentText.substring(position).match(/^<ScheduleEventAttendance\s[^>]*\/?>/i);
            if (scheduleEventAttendanceMatch) {
                const tag = scheduleEventAttendanceMatch[0];
                const scheduleEventAttendanceElement = this.parseScheduleEventAttendanceElement(tag);
                if (scheduleEventAttendanceElement) {
                    if (position > 0) {
                        const beforeText = currentText.substring(0, position);
                        if (beforeText.trim()) {
                            elements.push({ type: 'text', content: beforeText });
                        }
                    }
                    elements.push(scheduleEventAttendanceElement);
                    position += scheduleEventAttendanceMatch[0].length;
                    currentText = currentText.substring(position);
                    position = 0;
                    continue;
                }
            }
            // Look for SyncOption syntax <SyncOption ...>
            const syncOptionMatch = currentText.substring(position).match(/^<SyncOption\s[^>]*\/?>/i);
            if (syncOptionMatch) {
                const tag = syncOptionMatch[0];
                const syncOptionElement = this.parseSyncOptionElement(tag);
                if (syncOptionElement) {
                    if (position > 0) {
                        const beforeText = currentText.substring(0, position);
                        if (beforeText.trim()) {
                            elements.push({ type: 'text', content: beforeText });
                        }
                    }
                    elements.push(syncOptionElement);
                    position += syncOptionMatch[0].length;
                    currentText = currentText.substring(position);
                    position = 0;
                    continue;
                }
            }
            // Look for TimerDisplay syntax <TimerDisplay ...>
            const timerDisplayMatch = currentText.substring(position).match(/^<TimerDisplay\s[^>]*\/?>/i);
            if (timerDisplayMatch) {
                const tag = timerDisplayMatch[0];
                const timerDisplayElement = this.parseTimerDisplayElement(tag);
                if (timerDisplayElement) {
                    if (position > 0) {
                        const beforeText = currentText.substring(0, position);
                        if (beforeText.trim()) {
                            elements.push({ type: 'text', content: beforeText });
                        }
                    }
                    elements.push(timerDisplayElement);
                    position += timerDisplayMatch[0].length;
                    currentText = currentText.substring(position);
                    position = 0;
                    continue;
                }
            }
            // Look for TimerAudio syntax <TimerAudio ...>
            const timerAudioMatch = currentText.substring(position).match(/^<TimerAudio\s[^>]*\/?>/i);
            if (timerAudioMatch) {
                const tag = timerAudioMatch[0];
                const timerAudioElement = this.parseTimerAudioElement(tag);
                if (timerAudioElement) {
                    if (position > 0) {
                        const beforeText = currentText.substring(0, position);
                        if (beforeText.trim()) {
                            elements.push({ type: 'text', content: beforeText });
                        }
                    }
                    elements.push(timerAudioElement);
                    position += timerAudioMatch[0].length;
                    currentText = currentText.substring(position);
                    position = 0;
                    continue;
                }
            }
            // Look for portrait syntax <portrait:id> or <portrait:'''script''':variant>
            // First check for portrait with script and variant (e.g., <portrait:'''getCharacterPortrait("X")''':thumbnail>)
            const portraitWithScriptMatchWithVariant = currentText.substring(position).match(/^<portrait:('''[\s\S]+?'''):(thumbnail|card|large)>/);
            if (portraitWithScriptMatchWithVariant) {
                const scriptCode = portraitWithScriptMatchWithVariant[1].slice(3, -3);
                const variant = portraitWithScriptMatchWithVariant[2];
                if ((0, rich_text_1.isPortraitVariant)(variant)) {
                    if (position > 0) {
                        const beforeText = currentText.substring(0, position);
                        if (beforeText.trim()) {
                            elements.push({ type: 'text', content: beforeText });
                        }
                    }
                    elements.push({
                        type: 'image',
                        imageId: scriptCode,
                        imageType: 'portrait',
                        imageIdIsScript: true,
                        imageVariant: variant,
                        content: ''
                    });
                    position += portraitWithScriptMatchWithVariant[0].length;
                    currentText = currentText.substring(position);
                    position = 0;
                    continue;
                }
            }
            // Then check for portrait with variant (e.g., <portrait:image-id:thumbnail>)
            const portraitMatchWithVariant = currentText.substring(position).match(/^<portrait:([a-z0-9_:-]+):(thumbnail|card|large)>/);
            if (portraitMatchWithVariant) {
                const imageId = portraitMatchWithVariant[1];
                const variant = portraitMatchWithVariant[2];
                if (this.isValidImageId(imageId) && (0, rich_text_1.isPortraitVariant)(variant)) {
                    // Add any text before the portrait
                    if (position > 0) {
                        const beforeText = currentText.substring(0, position);
                        if (beforeText.trim()) {
                            elements.push({ type: 'text', content: beforeText });
                        }
                    }
                    // Add the portrait with variant
                    elements.push({
                        type: 'image',
                        imageId: imageId,
                        imageType: 'portrait',
                        imageVariant: variant,
                        content: ''
                    });
                    // Update position and currentText
                    position += portraitMatchWithVariant[0].length;
                    currentText = currentText.substring(position);
                    position = 0;
                    continue;
                }
            }
            // Then check for portrait without variant (defaults to card)
            const portraitMatch = currentText.substring(position).match(/^<portrait:([a-z0-9_:-]+)>/);
            if (portraitMatch) {
                const imageId = portraitMatch[1];
                if (this.isValidImageId(imageId)) {
                    // Add any text before the portrait
                    if (position > 0) {
                        const beforeText = currentText.substring(0, position);
                        if (beforeText.trim()) {
                            elements.push({ type: 'text', content: beforeText });
                        }
                    }
                    // Add the portrait
                    elements.push({
                        type: 'image',
                        imageId: imageId,
                        imageType: 'portrait',
                        content: ''
                    });
                    // Update position and currentText
                    position += portraitMatch[0].length;
                    currentText = currentText.substring(position);
                    position = 0;
                    continue;
                }
            }
            // Look for icon syntax <icon:id> or <icon:'''script_code'''> or <icon:'''script_code''':large>
            // First check for icon with script syntax and optional variant
            const iconWithScriptMatchWithVariant = currentText.substring(position).match(/^<icon:('''[^']+'''):(small|large)>/);
            if (iconWithScriptMatchWithVariant) {
                const scriptCode = iconWithScriptMatchWithVariant[1].slice(3, -3); // Remove ''' from both ends
                const variant = iconWithScriptMatchWithVariant[2];
                // Add any text before the icon
                if (position > 0) {
                    const beforeText = currentText.substring(0, position);
                    if (beforeText.trim()) {
                        elements.push({ type: 'text', content: beforeText });
                    }
                }
                // Add a special script-in-icon element
                elements.push({
                    type: 'image',
                    imageId: scriptCode,
                    imageType: 'icon',
                    imageIdIsScript: true, // Flag to indicate this needs script execution
                    imageVariant: variant,
                    content: ''
                });
                // Update position and currentText
                position += iconWithScriptMatchWithVariant[0].length;
                currentText = currentText.substring(position);
                position = 0;
                continue;
            }
            // Look for Divider syntax <Divider>
            const dividerInlineMatch = currentText.substring(position).match(/^<Divider>/);
            if (dividerInlineMatch) {
                if (position > 0) {
                    const beforeText = currentText.substring(0, position);
                    if (beforeText.trim()) {
                        elements.push({ type: 'text', content: beforeText });
                    }
                }
                elements.push({ type: 'divider' });
                position += dividerInlineMatch[0].length;
                currentText = currentText.substring(position);
                position = 0;
                continue;
            }
            // Then check for icon with script syntax without variant (defaults to small)
            const iconWithScriptMatch = currentText.substring(position).match(/^<icon:('''[^']+''')>/);
            if (iconWithScriptMatch) {
                const scriptCode = iconWithScriptMatch[1].slice(3, -3); // Remove ''' from both ends
                // Add any text before the icon
                if (position > 0) {
                    const beforeText = currentText.substring(0, position);
                    if (beforeText.trim()) {
                        elements.push({ type: 'text', content: beforeText });
                    }
                }
                // Add a special script-in-icon element
                elements.push({
                    type: 'image',
                    imageId: scriptCode,
                    imageType: 'icon',
                    imageIdIsScript: true, // Flag to indicate this needs script execution
                    content: ''
                });
                // Update position and currentText
                position += iconWithScriptMatch[0].length;
                currentText = currentText.substring(position);
                position = 0;
                continue;
            }
            // Then check for regular icon syntax with optional variant
            const iconMatchWithVariant = currentText.substring(position).match(/^<icon:([a-z0-9_:-]+):(small|large)>/);
            if (iconMatchWithVariant) {
                const imageId = iconMatchWithVariant[1];
                const variant = iconMatchWithVariant[2];
                if (this.isValidImageId(imageId)) {
                    // Add any text before the icon
                    if (position > 0) {
                        const beforeText = currentText.substring(0, position);
                        if (beforeText.trim()) {
                            elements.push({ type: 'text', content: beforeText });
                        }
                    }
                    // Add the icon
                    elements.push({
                        type: 'image',
                        imageId: imageId,
                        imageType: 'icon',
                        imageVariant: variant,
                        content: ''
                    });
                    // Update position and currentText
                    position += iconMatchWithVariant[0].length;
                    currentText = currentText.substring(position);
                    position = 0;
                    continue;
                }
            }
            // Then check for regular icon syntax without variant (defaults to small)
            const iconMatch = currentText.substring(position).match(/^<icon:([a-z0-9_:-]+)>/);
            if (iconMatch) {
                const imageId = iconMatch[1];
                if (this.isValidImageId(imageId)) {
                    // Add any text before the icon
                    if (position > 0) {
                        const beforeText = currentText.substring(0, position);
                        if (beforeText.trim()) {
                            elements.push({ type: 'text', content: beforeText });
                        }
                    }
                    // Add the icon
                    elements.push({
                        type: 'image',
                        imageId: imageId,
                        imageType: 'icon',
                        content: ''
                    });
                    // Update position and currentText
                    position += iconMatch[0].length;
                    currentText = currentText.substring(position);
                    position = 0;
                    continue;
                }
            }
            // Look for adventure syntax <adventure:adventure-id>
            // Stored as a dedicated element so the client loads the real TitleImage from the adventure header
            // (registry keys are not derivable from the adventure slug alone).
            // Path IDs (e.g. events/lapa-party/host) are allowed.
            const adventureMatch = currentText.substring(position).match(/^<adventure:([a-z0-9]+(?:[/-][a-z0-9]+)*)>/);
            if (adventureMatch) {
                const adventureId = adventureMatch[1];
                if (this.isValidAdventureId(adventureId)) {
                    // Add any text before the adventure
                    if (position > 0) {
                        const beforeText = currentText.substring(0, position);
                        if (beforeText.trim()) {
                            elements.push({ type: 'text', content: beforeText });
                        }
                    }
                    elements.push({
                        type: 'adventure',
                        adventureId,
                        content: '',
                    });
                    // Update position and currentText
                    position += adventureMatch[0].length;
                    currentText = currentText.substring(position);
                    position = 0;
                    continue;
                }
            }
            // Look for active-adventure syntax <active-adventure:session-id>
            const activeAdventureMatch = currentText.substring(position).match(/^<active-adventure:([a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})>/i);
            if (activeAdventureMatch) {
                const sessionId = activeAdventureMatch[1].toLowerCase(); // Normalize to lowercase
                if (this.isValidSessionId(sessionId)) {
                    // Add any text before the active adventure
                    if (position > 0) {
                        const beforeText = currentText.substring(0, position);
                        if (beforeText.trim()) {
                            elements.push({ type: 'text', content: beforeText });
                        }
                    }
                    // Add the active adventure element
                    elements.push({
                        type: 'active-adventure',
                        sessionId: sessionId,
                        content: ''
                    });
                    // Update position and currentText
                    position += activeAdventureMatch[0].length;
                    currentText = currentText.substring(position);
                    position = 0;
                    continue;
                }
            }
            // Look for markdown links [label](https://...)
            const linkMatch = currentText.substring(position).match(/^\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/);
            if (linkMatch) {
                if (position > 0) {
                    const beforeText = currentText.substring(0, position);
                    if (beforeText.trim()) {
                        elements.push({ type: 'text', content: beforeText });
                    }
                }
                elements.push({
                    type: 'link',
                    content: linkMatch[1],
                    href: linkMatch[2],
                });
                position += linkMatch[0].length;
                currentText = currentText.substring(position);
                position = 0;
                continue;
            }
            // Look for bold text **text** or __text__
            const boldMatch = currentText.substring(position).match(/^\*\*(.*?)\*\*|^__(.*?)__/);
            if (boldMatch) {
                // Add any text before the bold
                if (position > 0) {
                    const beforeText = currentText.substring(0, position);
                    if (beforeText.trim()) {
                        elements.push({ type: 'text', content: beforeText });
                    }
                }
                // Parse inner content so script blocks '''...''' and other inline formatting work
                const boldInner = (boldMatch[1] ?? boldMatch[2] ?? '');
                const boldParsed = this.parseInlineFormatting(boldInner);
                // Keep string for backward compat when content is single plain text
                const boldContent = boldParsed.length === 1 && boldParsed[0].type === 'text' && typeof boldParsed[0].content === 'string'
                    ? boldParsed[0].content
                    : boldParsed;
                elements.push({ type: 'bold', content: boldContent });
                // Update position and currentText
                position += boldMatch[0].length;
                currentText = currentText.substring(position);
                position = 0;
                continue;
            }
            // Skip variable references - they should be treated as regular text
            // This prevents the parser from splitting them into separate elements
            // Look for italic text *text* (removed _text_ to avoid conflicts with variable names)
            const italicMatch = currentText.substring(position).match(/^\*(.*?)\*/);
            if (italicMatch) {
                // Add any text before the italic
                if (position > 0) {
                    const beforeText = currentText.substring(0, position);
                    if (beforeText.trim()) {
                        elements.push({ type: 'text', content: beforeText });
                    }
                }
                // Parse inner content so script blocks '''...''' and other inline formatting work
                const italicInner = (italicMatch[1] ?? '');
                const italicParsed = this.parseInlineFormatting(italicInner);
                // Keep string for backward compat when content is single plain text
                const italicContent = italicParsed.length === 1 && italicParsed[0].type === 'text' && typeof italicParsed[0].content === 'string'
                    ? italicParsed[0].content
                    : italicParsed;
                elements.push({ type: 'italic', content: italicContent });
                // Update position and currentText
                position += italicMatch[0].length;
                currentText = currentText.substring(position);
                position = 0;
                continue;
            }
            position++;
        }
        // Add any remaining text
        if (currentText.trim()) {
            elements.push({ type: 'text', content: currentText });
        }
        // If no formatting was found, return a single text element
        if (elements.length === 0) {
            elements.push({ type: 'text', content: text });
        }
        return elements;
    }
    /**
     * Convert rich text back to plain text (for backward compatibility)
     */
    static richTextToPlainText(description) {
        return description.elements
            .map(element => this.elementToPlainText(element))
            .join('\n\n');
    }
    /**
     * Convert a single rich text element to plain text
     */
    static elementToPlainText(element) {
        if (typeof element === 'string') {
            return element;
        }
        // Handle image elements - return placeholder text
        if (element.type === 'image') {
            return `[${element.imageType}: ${element.imageId}]`;
        }
        // Handle inline option elements - return content with option indicator
        if (element.type === 'inline-option') {
            const content = Array.isArray(element.content)
                ? element.content.map((item) => this.elementToPlainText(item)).join('')
                : element.content || '';
            return `[OPTION: ${content}]`;
        }
        // Handle script elements - return placeholder text
        if (element.type === 'script') {
            return `[SCRIPT: ${element.scriptCode}]`;
        }
        // Handle adventure elements - return placeholder text
        if (element.type === 'adventure') {
            return `[ADVENTURE: ${element.adventureId}]`;
        }
        // Handle adventure-list elements - return placeholder text
        if (element.type === 'adventure-list') {
            return '[ADVENTURE-LIST]';
        }
        // Handle active-adventure elements - return placeholder text
        if (element.type === 'active-adventure') {
            return `[ACTIVE-ADVENTURE: ${element.sessionId}]`;
        }
        // Handle active-adventure-list elements - return placeholder text
        if (element.type === 'active-adventure-list') {
            return '[ACTIVE-ADVENTURE-LIST]';
        }
        // Handle user-input elements - return placeholder text
        if (element.type === 'user-input') {
            const variableName = element.userInputAttributes?.variable ?? '';
            return `[USER-INPUT: ${variableName}]`;
        }
        // Handle verification-code-input elements - return placeholder text
        if (element.type === 'verification-code-input') {
            const variableName = element.verificationCodeInputAttributes?.variable ?? '';
            return `[VERIFICATION-CODE-INPUT: ${variableName}]`;
        }
        // Handle spinner elements - return placeholder text
        if (element.type === 'spinner') {
            const variableName = element.spinnerAttributes?.variable ?? '';
            return `[SPINNER: ${variableName}]`;
        }
        // Handle color-picker elements - return placeholder text
        if (element.type === 'color-picker') {
            const variableName = element.colorPickerAttributes?.variable ?? '';
            return `[COLOR-PICKER: ${variableName}]`;
        }
        // Handle color-sample elements - return placeholder text
        if (element.type === 'color-sample') {
            const variableName = element.colorSampleAttributes?.variable ?? '';
            return `[COLOR-SAMPLE: ${variableName}]`;
        }
        // Handle code-display elements - return code with spaces between characters
        if (element.type === 'code-display') {
            const code = typeof element.content === 'string'
                ? element.content
                : element.codeDisplayAttributes?.variable ?? '';
            const spaced = Array.from(String(code || '').trim()).join(' ');
            return spaced || '[CODE-DISPLAY]';
        }
        // Handle time-picker elements - return placeholder text
        if (element.type === 'time-picker') {
            const variableName = element.timePickerAttributes?.variable ?? '';
            return `[TIME-PICKER: ${variableName}]`;
        }
        // Handle date-picker elements - return placeholder text
        if (element.type === 'date-picker') {
            const variableName = element.datePickerAttributes?.variable ?? '';
            return `[DATE-PICKER: ${variableName}]`;
        }
        // Handle divider elements - return placeholder text
        if (element.type === 'divider') {
            return '[DIVIDER]';
        }
        // Handle location-with-portraits elements - return placeholder text
        if (element.type === 'location-with-portraits') {
            const locationTag = element.locationWithPortraitsLocationTag ?? '(current)';
            return `[LOCATION-WITH-PORTRAITS: ${locationTag}]`;
        }
        // Handle character-comment elements
        if (element.type === 'character-comment') {
            const char = element.character ?? 'Unknown';
            const txt = element.text ?? '';
            return `[COMMENT: ${char}: ${txt}]`;
        }
        if (element.type === 'feedback-card') {
            const feedbackType = element.feedbackType ?? 'Comment';
            const title = element.feedbackTitle ?? '';
            const by = element.feedbackBy ?? 'Unknown';
            const scene = element.feedbackScene ?? '';
            const deployment = element.feedbackDeployment ?? '';
            const adv = element.feedbackAdventureDisplay ?? '';
            const extra = [scene && `scene=${scene}`, deployment && `deployment=${deployment}`, adv && `adventure=${adv}`]
                .filter(Boolean)
                .join(' ');
            return `[FEEDBACK: ${feedbackType}: ${title} by ${by}${extra ? ` ${extra}` : ''}]`;
        }
        if (element.type === 'analytics-table-row') {
            const position = element.analyticsPosition ?? '-';
            const name = element.analyticsName ?? 'Unknown';
            const value = element.analyticsValue ?? '0';
            return `[ANALYTICS: ${position} ${name} ${value}]`;
        }
        // Handle scroll-area elements
        if (element.type === 'scroll-area') {
            const children = element.content;
            const childText = Array.isArray(children)
                ? children.map((item) => this.elementToPlainText(item)).join('')
                : '';
            return childText;
        }
        if (element.type === 'conditional') {
            const trueText = element.children && element.children.length > 0
                ? this.richTextToPlainText({ elements: element.children })
                : '';
            const elseText = element.elseChildren && element.elseChildren.length > 0
                ? this.richTextToPlainText({ elements: element.elseChildren })
                : '';
            if (trueText && elseText) {
                return `${trueText}\n${elseText}`;
            }
            return trueText || elseText || '';
        }
        if (element.type === 'paragraph' && Array.isArray(element.content)) {
            return element.content.map((item) => this.elementToPlainText(item)).join('');
        }
        if (element.type === 'heading') {
            const prefix = '#'.repeat(element.level || 1) + ' ';
            if (typeof element.content === 'string') {
                return prefix + element.content;
            }
            else if (Array.isArray(element.content)) {
                return prefix + element.content.map((item) => this.elementToPlainText(item)).join('');
            }
            else {
                return prefix;
            }
        }
        if (element.type === 'list' && Array.isArray(element.content)) {
            return element.content
                .map((item, index) => {
                const prefix = element.listType === 'numbered' ? `${index + 1}. ` : '- ';
                return prefix + this.elementToPlainText(item);
            })
                .join('\n');
        }
        if (typeof element.content === 'string') {
            return element.content;
        }
        if (Array.isArray(element.content)) {
            return element.content.map((item) => this.elementToPlainText(item)).join('');
        }
        return '';
    }
    /**
     * Parse text as a paragraph without recursive calls
     */
    static parseTextAsParagraph(text) {
        const trimmed = text.trim();
        if (trimmed === '')
            return null;
        // Check for headings
        const headingMatch = trimmed.match(/^(#{1,3})\s+(.+)$/);
        if (headingMatch) {
            const level = headingMatch[1].length;
            const content = headingMatch[2].trim();
            return {
                type: 'heading',
                level: Math.min(level, 3), // Cap at level 3
                content: this.parseInlineFormatting(content)
            };
        }
        // Check for lists
        if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
            return this.parseBulletList(text);
        }
        if (/^\d+\.\s/.test(trimmed)) {
            return this.parseNumberedList(text);
        }
        // Check if this is a paragraph followed by a list
        const lines = trimmed.split('\n');
        if (lines.length > 1) {
            // Look for the first list line
            let listStartIndex = -1;
            for (let i = 0; i < lines.length; i++) {
                const line = lines[i].trim();
                if (line.startsWith('- ') || line.startsWith('* ') || /^\d+\.\s/.test(line)) {
                    listStartIndex = i;
                    break;
                }
            }
            if (listStartIndex > 0) {
                // Split into paragraph and list parts
                const paragraphLines = lines.slice(0, listStartIndex);
                const listLines = lines.slice(listStartIndex);
                const paragraphText = paragraphLines.join('\n').trim();
                const listText = listLines.join('\n').trim();
                // Return a combined element with paragraph and list
                const paragraphElement = {
                    type: 'paragraph',
                    content: this.parseInlineFormatting(paragraphText)
                };
                let listElement;
                if (listText.startsWith('- ') || listText.startsWith('* ')) {
                    listElement = this.parseBulletList(listText);
                }
                else {
                    listElement = this.parseNumberedList(listText);
                }
                return [paragraphElement, listElement];
            }
        }
        // Regular paragraph
        return {
            type: 'paragraph',
            content: this.parseInlineFormatting(trimmed)
        };
    }
    /**
     * Validate image ID format (for location images, portraits, and icons)
     */
    static isValidImageId(imageId) {
        // Image IDs can be:
        // 1. Simple format: lowercase alphanumeric with underscores (e.g., "forest_clearing", "adventurer_male", "magic_sword")
        // 2. Namespaced format: {adventure-id}:{image-id} (e.g., "dragon-slayer:hero", "events/lapa-party/host:entrance")
        if (imageId.includes(':')) {
            const colonIndex = imageId.indexOf(':');
            const adventureId = imageId.substring(0, colonIndex);
            const localId = imageId.substring(colonIndex + 1);
            if (!adventureId || !localId || localId.includes(':')) {
                return false;
            }
            const validAdventureId = /^[a-z0-9]+(?:[/-][a-z0-9]+)*$/.test(adventureId);
            return validAdventureId && /^[a-z0-9_]+$/.test(localId) && localId.length >= 3;
        }
        return /^[a-z0-9_]+$/.test(imageId) && imageId.length >= 3;
    }
    /**
     * Validate icon ID format specifically
     */
    static isValidIconId(iconId) {
        return this.isValidImageId(iconId);
    }
    /**
     * Validate adventure ID format
     * Adventure IDs are lowercase alphanumeric segments separated by hyphens or slashes
     * e.g., "dragon-slayer", "storynity", "events/lapa-party/host"
     */
    static isValidAdventureId(adventureId) {
        return /^[a-z0-9]+(?:[/-][a-z0-9]+)*$/.test(adventureId) && adventureId.length >= 3;
    }
    /**
     * Validate session ID format (UUID)
     * Session IDs must be valid UUIDs: 8-4-4-4-12 hexadecimal characters
     * e.g., "550e8400-e29b-41d4-a716-446655440000"
     */
    static isValidSessionId(sessionId) {
        // UUID format: 8-4-4-4-12 hexadecimal characters
        const uuidPattern = /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i;
        return uuidPattern.test(sessionId);
    }
    /**
     * Parse a CharacterComment tag into a rich text element.
     * Format: <CharacterComment character="X" portrait="Y" text="Z" timestamp="T">
     * Attributes character and text are required; portrait defaults to "generic", timestamp to "0".
     */
    static parseCharacterCommentElement(text) {
        const match = text.match(/^<CharacterComment\s([^>]*)>$/);
        if (!match) {
            return null;
        }
        const attrsText = match[1];
        const getAttr = (name) => {
            const re = new RegExp(`${name}="([^"]*)"`);
            const m = attrsText.match(re);
            return m ? m[1] : undefined;
        };
        const character = getAttr('character');
        const textVal = getAttr('text');
        return {
            type: 'character-comment',
            character: character ?? 'Unknown',
            portrait: getAttr('portrait') ?? 'generic',
            text: textVal ?? '',
            timestamp: getAttr('timestamp') ?? '0'
        };
    }
    /**
     * Parse a FeedbackCard tag into a rich text element.
     * Format: <FeedbackCard id="..." type="..." title="..." by="..." at="..." preview="..." selectionTarget="..." scene="..." deployment="..." adventure="...">
     */
    static parseFeedbackCardElement(text) {
        const match = text.match(/^<FeedbackCard\s([^>]*)>$/i);
        if (!match) {
            return null;
        }
        const attrsText = match[1];
        const getAttr = (name) => {
            const re = new RegExp(`${name}="([^"]*)"`);
            const m = attrsText.match(re);
            return m ? m[1] : undefined;
        };
        return {
            type: 'feedback-card',
            feedbackId: getAttr('id'),
            feedbackType: getAttr('type') ?? 'Comment',
            feedbackTitle: getAttr('title') ?? '',
            feedbackBy: getAttr('by') ?? 'Unknown',
            feedbackAt: getAttr('at') ?? '',
            feedbackPreview: getAttr('preview') ?? '',
            selectionTarget: getAttr('selectionTarget'),
            feedbackScene: getAttr('scene'),
            feedbackDeployment: getAttr('deployment'),
            feedbackAdventureDisplay: getAttr('adventure')
        };
    }
    /**
     * Parse an AnalyticsTableRow tag into a rich text element.
     * Format: <AnalyticsTableRow position="..." name="..." value="...">
     */
    static parseAnalyticsTableRowElement(text) {
        const match = text.match(/^<AnalyticsTableRow\s([^>]*)>$/i);
        if (!match) {
            return null;
        }
        const attrsText = match[1];
        const getAttr = (name) => {
            const re = new RegExp(`${name}="([^"]*)"`);
            const m = attrsText.match(re);
            return m ? m[1] : undefined;
        };
        return {
            type: 'analytics-table-row',
            analyticsPosition: getAttr('position') ?? '',
            analyticsName: getAttr('name') ?? '',
            analyticsValue: getAttr('value') ?? ''
        };
    }
    /**
     * Parse a ScheduleEvent tag into a rich text element.
     * Format: <ScheduleEvent id="..." title="..." location="..." start="..." duration="..." required="a,b" state="...">
     */
    static parseScheduleEventElement(text) {
        const match = text.match(/^<ScheduleEvent\s([^>]*)\/?>$/i);
        if (!match) {
            return null;
        }
        const attrsText = match[1];
        const getAttr = (name) => {
            const re = new RegExp(`${name}="([^"]*)"`);
            const m = attrsText.match(re);
            return m ? m[1] : undefined;
        };
        const requiredRaw = getAttr('required');
        const requiredCharacters = typeof requiredRaw === 'string' && requiredRaw.trim().length > 0
            ? requiredRaw.split(',').map(v => v.trim()).filter(Boolean)
            : [];
        const portraitsRaw = getAttr('portraits');
        const requiredPortraits = typeof portraitsRaw === 'string' && portraitsRaw.trim().length > 0
            ? portraitsRaw.split(',').map(v => v.trim()).filter(Boolean)
            : [];
        const unconfirmedRaw = getAttr('unconfirmedPortraits');
        const unconfirmedPortraits = typeof unconfirmedRaw === 'string' && unconfirmedRaw.trim().length > 0
            ? unconfirmedRaw.split(',').map(v => v.trim()).filter(Boolean)
            : [];
        const startRaw = getAttr('start');
        const durationRaw = getAttr('duration');
        return {
            type: 'schedule-event',
            scheduleEventId: getAttr('id'),
            scheduleTitle: getAttr('title') ?? 'Untitled event',
            scheduleLocation: getAttr('location') ?? 'Unknown location',
            scheduleLocationImageId: getAttr('locationImage'),
            scheduleStartTimeEpochSeconds: startRaw ? Number(startRaw) : undefined,
            scheduleDurationMinutes: durationRaw ? Number(durationRaw) : undefined,
            scheduleRequiredCharacters: requiredCharacters,
            scheduleRequiredPortraits: requiredPortraits,
            scheduleUnconfirmedPortraits: unconfirmedPortraits,
            scheduleState: getAttr('state') ?? 'proposed',
            scheduleInstruction: getAttr('instruction'),
            scheduleRealWorldLocation: getAttr('realWorldLocation'),
            selectionTarget: getAttr('selectionTarget')
        };
    }
    /**
     * Parse a ScheduleEventTime tag into a rich text element.
     * Format: <ScheduleEventTime eventId="..." start="..." selectionTarget="...">
     */
    static parseScheduleEventTimeElement(text) {
        const match = text.match(/^<ScheduleEventTime\s([^>]*)\/?>$/i);
        if (!match) {
            return null;
        }
        const attrsText = match[1];
        const getAttr = (name) => {
            const re = new RegExp(`${name}="([^"]*)"`);
            const m = attrsText.match(re);
            return m ? m[1] : undefined;
        };
        const startRaw = getAttr('start');
        const acceptedRaw = getAttr('accepted');
        const acceptedPortraits = typeof acceptedRaw === 'string' && acceptedRaw.trim().length > 0
            ? acceptedRaw.split(',').map(v => v.trim()).filter(Boolean)
            : [];
        const requiredPortraitsRaw = getAttr('requiredPortraits');
        const requiredPortraits = typeof requiredPortraitsRaw === 'string' && requiredPortraitsRaw.trim().length > 0
            ? requiredPortraitsRaw.split(',').map(v => v.trim()).filter(Boolean)
            : [];
        return {
            type: 'schedule-event-time',
            scheduleEventId: getAttr('eventId'),
            scheduleEventTimeEpochSeconds: startRaw ? Number(startRaw) : undefined,
            scheduleRequiredPortraits: requiredPortraits,
            scheduleAcceptedPortraits: acceptedPortraits,
            scheduleRealWorldLocation: getAttr('realWorldLocation'),
            selectionTarget: getAttr('selectionTarget')
        };
    }
    /**
     * Parse a ScheduleEventAttendance tag into a rich text element.
     * Format: <ScheduleEventAttendance portraits="p1,p2,p3" absentPortraits="p2,p3">
     * Renders required character portraits; those in absentPortraits are shown greyed out.
     */
    static parseScheduleEventAttendanceElement(text) {
        const match = text.match(/^<ScheduleEventAttendance\s([^>]*)\/?>$/i);
        if (!match) {
            return null;
        }
        const attrsText = match[1];
        const getAttr = (name) => {
            const re = new RegExp(`${name}="([^"]*)"`);
            const m = attrsText.match(re);
            return m ? m[1] : undefined;
        };
        const portraitsRaw = getAttr('portraits');
        const requiredPortraits = typeof portraitsRaw === 'string' && portraitsRaw.trim().length > 0
            ? portraitsRaw.split(',').map(v => v.trim()).filter(Boolean)
            : [];
        const absentRaw = getAttr('absentPortraits');
        const absentPortraits = typeof absentRaw === 'string' && absentRaw.trim().length > 0
            ? absentRaw.split(',').map(v => v.trim()).filter(Boolean)
            : [];
        return {
            type: 'schedule-event-attendance',
            scheduleRequiredPortraits: requiredPortraits,
            scheduleAbsentPortraits: absentPortraits
        };
    }
    /**
     * Parse a SyncOption tag into a rich text element.
     * Format: <SyncOption text="..." syncVar="..." target="..." characters="..." portraits="..." unclickedPortraits="...">
     * Output of getCharacterSynchronisationWaitOption. unclickedPortraits is optional.
     */
    static parseSyncOptionElement(text) {
        const match = text.match(/^<SyncOption\s([^>]*)\/?>$/i);
        if (!match) {
            return null;
        }
        const attrsText = match[1];
        const getAttr = (name) => {
            const re = new RegExp(`${name}="([^"]*)"`);
            const m = attrsText.match(re);
            return m ? m[1] : undefined;
        };
        const textAttr = getAttr('text');
        const syncVar = getAttr('syncVar');
        const target = getAttr('target');
        const targetByCharacterRaw = getAttr('targetByCharacter');
        const charactersRaw = getAttr('characters');
        const portraitsRaw = getAttr('portraits');
        const unclickedRaw = getAttr('unclickedPortraits');
        const syncEffectsRaw = getAttr('syncEffects');
        if (!syncVar || !charactersRaw) {
            return null;
        }
        if (!target && !targetByCharacterRaw) {
            return null;
        }
        const syncCharacters = typeof charactersRaw === 'string' && charactersRaw.trim().length > 0
            ? charactersRaw.split(',').map((v) => v.trim()).filter(Boolean)
            : [];
        const syncPortraits = typeof portraitsRaw === 'string' && portraitsRaw.trim().length > 0
            ? portraitsRaw.split(',').map((v) => v.trim()).filter(Boolean)
            : [];
        const syncUnclickedPortraits = typeof unclickedRaw === 'string' && unclickedRaw.trim().length > 0
            ? unclickedRaw.split(',').map((v) => v.trim()).filter(Boolean)
            : undefined;
        let syncTargetByCharacter;
        if (typeof targetByCharacterRaw === 'string' && targetByCharacterRaw.trim().length > 0) {
            syncTargetByCharacter = {};
            for (const part of targetByCharacterRaw.split(',').map((p) => p.trim())) {
                const colonIdx = part.indexOf(':');
                if (colonIdx > 0) {
                    const charName = part.substring(0, colonIdx).trim();
                    const sceneId = part.substring(colonIdx + 1).trim();
                    if (charName && sceneId)
                        syncTargetByCharacter[charName] = sceneId;
                }
            }
            if (Object.keys(syncTargetByCharacter).length === 0)
                syncTargetByCharacter = undefined;
        }
        const syncOptionEffects = typeof syncEffectsRaw === 'string' && syncEffectsRaw.trim().length > 0
            ? syncEffectsRaw
                .trim()
                .replace(/&quot;/g, '"')
                .replace(/&lt;/g, '<')
                .replace(/&gt;/g, '>')
                .replace(/&amp;/g, '&')
            : undefined;
        return {
            type: 'sync-option',
            syncOptionText: textAttr ?? 'Ready',
            syncVar,
            target: target ?? (syncTargetByCharacter && syncCharacters[0] ? syncTargetByCharacter[syncCharacters[0]] : ''),
            syncCharacters,
            syncPortraits,
            syncUnclickedPortraits,
            syncTargetByCharacter,
            syncOptionEffects
        };
    }
    /**
     * Parse a TimerDisplay tag into a rich text element.
     * Format: <TimerDisplay timerVar="..." type="blind|stopwatch|countdown|min" />
     * Output of displayTimer(). type is optional (default from timer state when rendered).
     */
    static parseTimerDisplayElement(text) {
        const match = text.match(/^<TimerDisplay\s([^>]*)\/?>$/i);
        if (!match) {
            return null;
        }
        const attrsText = match[1];
        const getAttr = (name) => {
            const re = new RegExp(`${name}="([^"]*)"`);
            const m = attrsText.match(re);
            return m ? m[1] : undefined;
        };
        const timerVar = getAttr('timerVar');
        const typeAttr = getAttr('type');
        const startedAt = getAttr('startedAt');
        const stoppedAt = getAttr('stoppedAt');
        const maxSeconds = getAttr('maxSeconds');
        const targetAt = getAttr('targetAt');
        const armedAt = getAttr('armedAt');
        const preCountdownSeconds = getAttr('preCountdownSeconds');
        const preCountdownAudioId = getAttr('preCountdownAudioId');
        if (!timerVar || timerVar.trim().length === 0) {
            return null;
        }
        const validTypes = ['blind', 'stopwatch', 'countdown', 'min', 'deadline-countdown'];
        const timerDisplayType = typeAttr !== undefined && typeAttr.trim().length > 0 && validTypes.includes(typeAttr.trim())
            ? typeAttr.trim()
            : undefined;
        const decodeAttr = (value) => value
            .replace(/&quot;/g, '"')
            .replace(/&amp;/g, '&')
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>');
        const out = {
            type: 'timer-display',
            timerVar: timerVar.trim(),
            timerDisplayType,
            content: ''
        };
        if (startedAt !== undefined && startedAt.trim().length > 0)
            out.startedAt = startedAt.trim();
        if (stoppedAt !== undefined && stoppedAt.trim().length > 0)
            out.stoppedAt = stoppedAt.trim();
        if (maxSeconds !== undefined && maxSeconds.trim().length > 0)
            out.maxSeconds = maxSeconds.trim();
        if (targetAt !== undefined && targetAt.trim().length > 0)
            out.targetAt = targetAt.trim();
        if (armedAt !== undefined && armedAt.trim().length > 0)
            out.armedAt = armedAt.trim();
        if (preCountdownSeconds !== undefined && preCountdownSeconds.trim().length > 0) {
            out.preCountdownSeconds = preCountdownSeconds.trim();
        }
        if (preCountdownAudioId !== undefined && preCountdownAudioId.trim().length > 0) {
            out.preCountdownAudioId = decodeAttr(preCountdownAudioId.trim());
        }
        return out;
    }
    /**
     * Parse a TimerAudio tag into a rich text element.
     * Format: <TimerAudio timerVar="..." />
     * Output of displayTimerAudio(). Optional startedAt, stoppedAt, maxSeconds, targetAt for client scheduling.
     */
    static parseTimerAudioElement(text) {
        const match = text.match(/^<TimerAudio\s([^>]*)\/?>$/i);
        if (!match) {
            return null;
        }
        const attrsText = match[1];
        const getAttr = (name) => {
            const re = new RegExp(`${name}="([^"]*)"`);
            const m = attrsText.match(re);
            return m ? m[1] : undefined;
        };
        const timerVar = getAttr('timerVar');
        const startedAt = getAttr('startedAt');
        const stoppedAt = getAttr('stoppedAt');
        const maxSeconds = getAttr('maxSeconds');
        const targetAt = getAttr('targetAt');
        const soundCues = getAttr('soundCues');
        const armedAt = getAttr('armedAt');
        const preCountdownSeconds = getAttr('preCountdownSeconds');
        const preCountdownAudioId = getAttr('preCountdownAudioId');
        if (!timerVar || timerVar.trim().length === 0) {
            return null;
        }
        const decodeAttr = (value) => value
            .replace(/&quot;/g, '"')
            .replace(/&amp;/g, '&')
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>');
        const out = {
            type: 'timer-audio',
            timerVar: timerVar.trim(),
            content: ''
        };
        if (startedAt !== undefined && startedAt.trim().length > 0)
            out.startedAt = startedAt.trim();
        if (stoppedAt !== undefined && stoppedAt.trim().length > 0)
            out.stoppedAt = stoppedAt.trim();
        if (maxSeconds !== undefined && maxSeconds.trim().length > 0)
            out.maxSeconds = maxSeconds.trim();
        if (targetAt !== undefined && targetAt.trim().length > 0)
            out.targetAt = targetAt.trim();
        if (soundCues !== undefined && soundCues.trim().length > 0) {
            out.soundCues = decodeAttr(soundCues.trim());
        }
        if (armedAt !== undefined && armedAt.trim().length > 0)
            out.armedAt = armedAt.trim();
        if (preCountdownSeconds !== undefined && preCountdownSeconds.trim().length > 0) {
            out.preCountdownSeconds = preCountdownSeconds.trim();
        }
        if (preCountdownAudioId !== undefined && preCountdownAudioId.trim().length > 0) {
            out.preCountdownAudioId = decodeAttr(preCountdownAudioId.trim());
        }
        return out;
    }
    /**
     * Parse a JoinEventCard tag into a rich text element.
     * Format: <JoinEventCard id="..." title="..." start="..." startScene="...">
     */
    static parseJoinEventCardElement(text) {
        const match = text.match(/^<JoinEventCard\s([^>]*)\/?>$/i);
        if (!match) {
            return null;
        }
        const attrsText = match[1];
        const getAttr = (name) => {
            const re = new RegExp(`${name}="([^"]*)"`);
            const m = attrsText.match(re);
            return m ? m[1] : undefined;
        };
        const startRaw = getAttr('start');
        return {
            type: 'join-event-card',
            scheduleEventId: getAttr('id'),
            scheduleTitle: getAttr('title') ?? 'Untitled event',
            scheduleStartTimeEpochSeconds: startRaw ? Number(startRaw) : undefined,
            selectionTarget: getAttr('startScene')
        };
    }
    /**
     * Parse an Active-Adventure-List tag into a rich text element.
     * Format: <Active-Adventure-List tags=collabajam-band contentVariable=active_list_content>
     */
    static parseActiveAdventureListElement(text) {
        const match = text.trim().match(/^<Active-Adventure-List\b([^>]*?)\s*\/?>$/);
        if (!match) {
            return null;
        }
        const allowedKeys = new Set(['tags', 'contentVariable', 'labelVariable']);
        const attributesMap = this.parseGenericAttributes((match[1] || '').trim(), allowedKeys);
        if (!attributesMap) {
            return null;
        }
        const tags = attributesMap.get('tags');
        if (tags !== undefined && !/^[a-zA-Z0-9_,-]+$/.test(tags)) {
            return null;
        }
        // Prefer contentVariable; accept legacy labelVariable as an alias
        const contentVariable = attributesMap.get('contentVariable') ?? attributesMap.get('labelVariable');
        if (contentVariable !== undefined && !this.isValidVariableIdentifier(contentVariable)) {
            return null;
        }
        const element = {
            type: 'active-adventure-list',
            content: ''
        };
        if (tags) {
            element.adventureListTags = tags;
        }
        if (contentVariable) {
            element.adventureListContentVariable = contentVariable;
        }
        return element;
    }
    /**
     * Parse a Guest-Adventure-List tag into a rich text element.
     * Format: <Guest-Adventure-List selectionTarget=guest_detail statusVariable=rsvp_status statusLabels="yes:Accepted,:Awaiting response" />
     */
    static parseGuestAdventureListElement(text) {
        const match = text.trim().match(/^<Guest-Adventure-List\b([^>]*?)\s*\/?>$/);
        if (!match) {
            return null;
        }
        const allowedKeys = new Set([
            'selectionTarget',
            'selectionVariable',
            'statusVariable',
            'statusLabels',
            'statusFilter'
        ]);
        const attributesMap = this.parseGenericAttributes((match[1] || '').trim(), allowedKeys);
        if (!attributesMap) {
            return null;
        }
        const selectionTarget = attributesMap.get('selectionTarget');
        const statusVariable = attributesMap.get('statusVariable');
        if (statusVariable && !this.isValidVariableIdentifier(statusVariable)) {
            return null;
        }
        return {
            type: 'guest-adventure-list',
            selectionTarget,
            keyedListSelectionVariable: attributesMap.get('selectionVariable') ?? 'selected_guest_id',
            guestAdventureListStatusVariable: statusVariable,
            guestAdventureListStatusLabels: attributesMap.get('statusLabels'),
            guestAdventureListStatusFilter: attributesMap.get('statusFilter'),
            content: ''
        };
    }
    /**
     * Parse a Verification-Code-Input tag into a rich text element
     * Format: <Verification-Code-Input variable=mail_room_verification_code placeholder="Optional" />
     */
    static parseVerificationCodeInputElement(text) {
        const match = text.match(/^<Verification-Code-Input\b([^>]*)\/>$/);
        if (!match) {
            return null;
        }
        const attributesText = (match[1] || '').trim();
        const allowedKeys = new Set(['variable', 'placeholder']);
        const attributesMap = this.parseGenericAttributes(attributesText, allowedKeys);
        if (!attributesMap) {
            return null;
        }
        const variable = attributesMap.get('variable');
        if (!variable || !this.isValidVariableIdentifier(variable)) {
            return null;
        }
        const placeholder = attributesMap.get('placeholder');
        const out = { variable };
        if (placeholder !== undefined)
            out.placeholder = placeholder;
        return {
            type: 'verification-code-input',
            verificationCodeInputAttributes: out,
            content: ''
        };
    }
    /**
     * Parse a User-Input tag into a rich text element
     */
    static parseUserInputElement(text) {
        const match = text.match(/^<User-Input\b([^>]*)\/>$/);
        if (!match) {
            return null;
        }
        const attributesText = (match[1] || '').trim();
        const attributes = this.parseUserInputAttributes(attributesText);
        if (!attributes) {
            return null;
        }
        return {
            type: 'user-input',
            userInputAttributes: attributes,
            content: ''
        };
    }
    /**
     * Parse and validate User-Input attributes
     */
    static parseUserInputAttributes(attributesText) {
        if (!attributesText) {
            return null;
        }
        const allowedKeys = new Set(['variable', 'placeholder', 'maxLength', 'autoSave', 'debounce', 'confirmationTarget', 'type', 'multiline', 'rows']);
        const attributesMap = new Map();
        let index = 0;
        while (index < attributesText.length) {
            // Skip whitespace
            while (index < attributesText.length && /\s/.test(attributesText[index])) {
                index++;
            }
            if (index >= attributesText.length) {
                break;
            }
            const keyStart = index;
            while (index < attributesText.length && /[a-zA-Z]/.test(attributesText[index])) {
                index++;
            }
            if (keyStart === index) {
                return null; // Invalid attribute key
            }
            const key = attributesText.slice(keyStart, index);
            if (!allowedKeys.has(key) || attributesMap.has(key)) {
                return null;
            }
            // Skip whitespace before equals
            while (index < attributesText.length && /\s/.test(attributesText[index])) {
                index++;
            }
            if (attributesText[index] !== '=') {
                return null;
            }
            index++; // Skip '='
            // Skip whitespace before value
            while (index < attributesText.length && /\s/.test(attributesText[index])) {
                index++;
            }
            if (index >= attributesText.length) {
                return null;
            }
            let value = '';
            const quoteChar = attributesText[index];
            if (quoteChar === '"' || quoteChar === "'") {
                index++;
                const valueStart = index;
                while (index < attributesText.length && attributesText[index] !== quoteChar) {
                    index++;
                }
                if (index >= attributesText.length) {
                    return null; // Unterminated quote
                }
                value = attributesText.slice(valueStart, index);
                index++; // Skip closing quote
            }
            else {
                const valueStart = index;
                while (index < attributesText.length && !/\s/.test(attributesText[index])) {
                    index++;
                }
                value = attributesText.slice(valueStart, index);
            }
            attributesMap.set(key, value);
        }
        const variable = attributesMap.get('variable');
        if (!variable || !this.isValidVariableIdentifier(variable)) {
            return null;
        }
        const result = { variable };
        const placeholder = attributesMap.get('placeholder');
        if (placeholder !== undefined) {
            result.placeholder = placeholder;
        }
        const maxLengthValue = attributesMap.get('maxLength');
        if (maxLengthValue !== undefined) {
            if (!/^\d+$/.test(maxLengthValue)) {
                return null;
            }
            const maxLength = Number(maxLengthValue);
            if (!Number.isInteger(maxLength) || maxLength <= 0) {
                return null;
            }
            result.maxLength = maxLength;
        }
        const autoSaveValue = attributesMap.get('autoSave');
        if (autoSaveValue !== undefined) {
            if (autoSaveValue !== 'true' && autoSaveValue !== 'false') {
                return null;
            }
            result.autoSave = autoSaveValue === 'true';
        }
        const debounceValue = attributesMap.get('debounce');
        if (debounceValue !== undefined) {
            if (!/^\d+$/.test(debounceValue)) {
                return null;
            }
            const debounce = Number(debounceValue);
            if (!Number.isInteger(debounce) || debounce < 0) {
                return null;
            }
            result.debounce = debounce;
        }
        const confirmationTarget = attributesMap.get('confirmationTarget');
        if (confirmationTarget !== undefined) {
            if (!/^[a-zA-Z0-9_-]+$/.test(confirmationTarget)) {
                return null;
            }
            result.confirmationTarget = confirmationTarget;
        }
        const type = attributesMap.get('type');
        if (type !== undefined) {
            // Validate type is a valid HTML input type
            const validTypes = ['text', 'password', 'email', 'number', 'tel', 'url', 'search'];
            if (!validTypes.includes(type)) {
                return null;
            }
            result.type = type;
        }
        const multilineValue = attributesMap.get('multiline');
        if (multilineValue !== undefined) {
            if (multilineValue !== 'true' && multilineValue !== 'false') {
                return null;
            }
            result.multiline = multilineValue === 'true';
        }
        const rowsValue = attributesMap.get('rows');
        if (rowsValue !== undefined) {
            if (!/^\d+$/.test(rowsValue)) {
                return null;
            }
            const rows = Number(rowsValue);
            if (!Number.isInteger(rows) || rows < 1 || rows > 20) {
                return null;
            }
            result.rows = rows;
        }
        return result;
    }
    /**
     * Parse a Spinner tag into a rich text element
     */
    static parseSpinnerElement(text) {
        const match = text.match(/^<Spinner\b([^>]*)\/>$/);
        if (!match) {
            return null;
        }
        const attributesText = (match[1] || '').trim();
        const attributes = this.parseSpinnerAttributes(attributesText);
        if (!attributes) {
            return null;
        }
        return {
            type: 'spinner',
            spinnerAttributes: attributes,
            content: ''
        };
    }
    /**
     * Parse and validate Spinner attributes
     */
    static parseSpinnerAttributes(attributesText) {
        if (!attributesText) {
            return null;
        }
        const allowedKeys = new Set(['variable', 'values', 'default', 'order']);
        const attributesMap = this.parseGenericAttributes(attributesText, allowedKeys);
        if (!attributesMap) {
            return null;
        }
        const variable = attributesMap.get('variable');
        if (!variable || !this.isValidVariableIdentifier(variable)) {
            return null;
        }
        const rawValues = attributesMap.get('values');
        if (!rawValues || rawValues.trim() === '') {
            return null;
        }
        const values = (0, activitySpinnerMarkup_1.decodeSpinnerAttributeValue)(rawValues);
        const valuesList = values.split(',').map((v) => v.trim()).filter(Boolean);
        if (valuesList.length === 0) {
            return null;
        }
        const result = { variable, values };
        const orderAttr = attributesMap.get('order');
        if (orderAttr === 'ascending') {
            result.order = 'ascending';
        }
        const defaultValue = attributesMap.get('default');
        if (defaultValue !== undefined && defaultValue.trim() !== '') {
            const trimmedDefault = (0, activitySpinnerMarkup_1.decodeSpinnerAttributeValue)(defaultValue.trim());
            if (valuesList.includes(trimmedDefault)) {
                result.default = trimmedDefault;
            }
            else if (/^\d+$/.test(trimmedDefault)) {
                const asIndex = Number(trimmedDefault);
                if (Number.isInteger(asIndex) && asIndex >= 0 && asIndex < valuesList.length) {
                    result.default = valuesList[asIndex];
                }
            }
        }
        return result;
    }
    /**
     * Split a `colors` attribute into CSS color tokens. Commas inside parentheses (e.g. rgb(), rgba(), hsl())
     * do not start a new token.
     */
    static splitColorPickerColorTokens(colors) {
        return (0, splitColorPickerColorTokens_1.splitColorPickerColorTokens)(colors);
    }
    /**
     * Parse a Color-Picker tag into a rich text element
     */
    static parseColorPickerElement(text) {
        const match = text.match(/^<Color-Picker\b([^>]*)\/>$/);
        if (!match) {
            return null;
        }
        const attributesText = (match[1] || '').trim();
        const attributes = this.parseColorPickerAttributes(attributesText);
        if (!attributes) {
            return null;
        }
        return {
            type: 'color-picker',
            colorPickerAttributes: attributes,
            content: ''
        };
    }
    /**
     * Parse and validate Color-Picker attributes
     */
    static parseColorPickerAttributes(attributesText) {
        if (!attributesText) {
            return null;
        }
        const allowedKeys = new Set(['variable', 'colors', 'selectionTarget']);
        const attributesMap = this.parseGenericAttributes(attributesText, allowedKeys);
        if (!attributesMap) {
            return null;
        }
        const variable = attributesMap.get('variable');
        if (!variable || !this.isValidVariableIdentifier(variable)) {
            return null;
        }
        const colors = attributesMap.get('colors');
        if (!colors || colors.trim() === '') {
            return null;
        }
        const tokens = this.splitColorPickerColorTokens(colors);
        if (tokens.length === 0) {
            return null;
        }
        const selectionTarget = attributesMap.get('selectionTarget');
        if (!selectionTarget || !/^[a-zA-Z0-9_-]+$/.test(selectionTarget)) {
            return null;
        }
        return { variable, colors, selectionTarget };
    }
    /**
     * Parse a Color-Sample tag: read-only block filled from variable (CSS color value).
     */
    static parseColorSampleElement(text) {
        const match = text.match(/^<Color-Sample\b([^>]*)\/>$/);
        if (!match) {
            return null;
        }
        const attributesText = (match[1] || '').trim();
        const attributes = this.parseColorSampleAttributes(attributesText);
        if (!attributes) {
            return null;
        }
        return {
            type: 'color-sample',
            colorSampleAttributes: attributes,
            content: ''
        };
    }
    static parseColorSampleAttributes(attributesText) {
        if (!attributesText) {
            return null;
        }
        const allowedKeys = new Set(['variable']);
        const attributesMap = this.parseGenericAttributes(attributesText, allowedKeys);
        if (!attributesMap) {
            return null;
        }
        const variable = attributesMap.get('variable');
        if (!variable || !this.isValidVariableIdentifier(variable)) {
            return null;
        }
        return { variable };
    }
    /**
     * Parse a Code-Display tag: read-only large characters from variable (code string).
     */
    static parseCodeDisplayElement(text) {
        const match = text.match(/^<Code-Display\b([^>]*)\/>$/);
        if (!match) {
            return null;
        }
        const attributesText = (match[1] || '').trim();
        const attributes = this.parseCodeDisplayAttributes(attributesText);
        if (!attributes) {
            return null;
        }
        return {
            type: 'code-display',
            codeDisplayAttributes: attributes,
            content: ''
        };
    }
    static parseCodeDisplayAttributes(attributesText) {
        if (!attributesText) {
            return null;
        }
        const allowedKeys = new Set(['variable']);
        const attributesMap = this.parseGenericAttributes(attributesText, allowedKeys);
        if (!attributesMap) {
            return null;
        }
        const variable = attributesMap.get('variable');
        if (!variable || !this.isValidVariableIdentifier(variable)) {
            return null;
        }
        return { variable };
    }
    /**
     * Parse a Location-With-Portraits tag into a rich text element
     * Syntax: <Location-With-Portraits location=lounge /> or <Location-With-Portraits location='''getCurrentLocation()''' /> or <Location-With-Portraits />
     * When location is omitted, uses getCurrentLocation() at interpolation time.
     * Image is resolved from Location.locationImageId (if defined) or adventureId:locationTag convention.
     */
    static parseLocationWithPortraitsElement(text) {
        const match = text.match(/^<Location-With-Portraits\b([^>]*)\/>$/i);
        if (!match) {
            return null;
        }
        const attributesText = (match[1] || '').trim();
        if (!attributesText) {
            return { type: 'location-with-portraits', content: '' };
        }
        const scriptMatch = attributesText.match(/^\s*location=('''[\s\S]+?''')\s*$/);
        if (scriptMatch) {
            const scriptCode = scriptMatch[1].slice(3, -3).trim();
            if (!scriptCode)
                return null;
            return {
                type: 'location-with-portraits',
                locationWithPortraitsLocationTag: scriptMatch[1],
                locationWithPortraitsLocationTagIsScript: true,
                content: ''
            };
        }
        const allowedKeys = new Set(['location']);
        const attributesMap = this.parseGenericAttributes(attributesText, allowedKeys);
        if (!attributesMap) {
            return null;
        }
        const locationTag = attributesMap.get('location');
        if (!locationTag) {
            return { type: 'location-with-portraits', content: '' };
        }
        if (!/^[a-zA-Z0-9_-]+$/.test(locationTag)) {
            return null;
        }
        return {
            type: 'location-with-portraits',
            locationWithPortraitsLocationTag: locationTag,
            content: ''
        };
    }
    /**
     * Parse Activity-List embed: <Activity-List types=task,sub_task parent=foo sort=list_order />
     */
    static parseActivityListElement(text) {
        const match = text.match(/^<Activity-List\b([^>]*)\/>$/i) || text.match(/^<Activity-List\b([^>]*)>$/i);
        if (!match) {
            return null;
        }
        const inner = (match[1] || '').trim();
        const attrs = {};
        const re = /(\w+)=([^\s/>]+)/g;
        let m;
        while ((m = re.exec(inner)) !== null) {
            attrs[m[1].toLowerCase()] = m[2];
        }
        const sortRaw = (attrs.sort || 'list_order').toLowerCase();
        const sort = sortRaw === 'name' ? 'name' : 'list_order';
        const hideLockedRaw = (attrs.hide_locked || '').toLowerCase();
        const activityListHideLocked = hideLockedRaw === 'true' || hideLockedRaw === '1' || hideLockedRaw === 'yes';
        const hideRequirementsRaw = (attrs.hide_requirements || '').toLowerCase();
        const activityListHideRequirements = hideRequirementsRaw === 'true' ||
            hideRequirementsRaw === '1' ||
            hideRequirementsRaw === 'yes';
        const groupCompletedRaw = (attrs.group_completed || '').toLowerCase();
        const activityListGroupCompleted = groupCompletedRaw === 'false' || groupCompletedRaw === '0' || groupCompletedRaw === 'no'
            ? false
            : undefined;
        const selectionTarget = (attrs.selectiontarget || '').trim();
        const selectionVariable = (attrs.selectionvariable || '').trim();
        const selectionSyncCharacters = (attrs.selection_sync_characters ||
            attrs.selectionsynccharacters ||
            '').trim();
        const allowLockedRaw = (attrs.selectionallowlocked || '').toLowerCase();
        const activityListSelectionAllowLocked = allowLockedRaw === 'true' || allowLockedRaw === '1' || allowLockedRaw === 'yes';
        const redoSceneRaw = (attrs.redo_scene || '').trim();
        if ((selectionTarget && !selectionVariable) ||
            (!selectionTarget && selectionVariable)) {
            return null;
        }
        if (selectionTarget &&
            (!/^[a-zA-Z0-9_-]+$/.test(selectionTarget) ||
                !/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(selectionVariable))) {
            return null;
        }
        if (redoSceneRaw && !/^[a-zA-Z0-9_-]+$/.test(redoSceneRaw)) {
            return null;
        }
        return {
            type: 'activity-list',
            activityListTypes: attrs.types,
            activityListParent: attrs.parent,
            activityListSort: sort,
            ...(activityListHideLocked ? { activityListHideLocked: true } : {}),
            ...(activityListHideRequirements ? { activityListHideRequirements: true } : {}),
            ...(activityListGroupCompleted === false ? { activityListGroupCompleted: false } : {}),
            ...(selectionTarget ? { activityListSelectionTarget: selectionTarget } : {}),
            ...(selectionVariable ? { activityListSelectionVariable: selectionVariable } : {}),
            ...(selectionSyncCharacters
                ? { activityListSelectionSyncCharacters: selectionSyncCharacters }
                : {}),
            ...(activityListSelectionAllowLocked ? { activityListSelectionAllowLocked: true } : {}),
            ...(redoSceneRaw ? { activityListRedoScene: redoSceneRaw } : {}),
            content: ''
        };
    }
    /**
     * Parse Entity-List embed: <Entity-List types=archive parent=current sort=name />
     * Foreign library lists: source=adventure:… owner=self|ownerVariable=… (reference-only).
     */
    static parseEntityListElement(text) {
        const match = text.match(/^<Entity-List\b([^>]*)\/>$/i) || text.match(/^<Entity-List\b([^>]*)>$/i);
        if (!match) {
            return null;
        }
        const inner = (match[1] || '').trim();
        const attrs = {};
        // Allow `/` and `:` in unquoted values; quoted values for messages with spaces.
        const re = /(\w+)=(?:"([^"]*)"|'([^']*)'|([^\s>]+))/g;
        let m;
        while ((m = re.exec(inner)) !== null) {
            const key = m[1].toLowerCase();
            if (m[2] !== undefined) {
                attrs[key] = m[2];
            }
            else if (m[3] !== undefined) {
                attrs[key] = m[3];
            }
            else {
                // Strip a trailing `/` from self-closing tags captured into the value.
                attrs[key] = (m[4] || '').replace(/\/$/, '');
            }
        }
        const sortRaw = (attrs.sort || 'name').toLowerCase();
        const sort = sortRaw === 'created' ? 'created' : 'name';
        const parent = (attrs.parent || '').trim();
        const parentVariable = (attrs.parentvariable || '').trim();
        const source = (attrs.source || '').trim();
        const owner = (attrs.owner || '').trim();
        const ownerVariable = (attrs.ownervariable || '').trim();
        const isForeign = source.length > 0;
        // Local lists: keep strict parse-time rejection for ambiguous parent / unpaired selection.
        // Foreign lists: allow through so AdventureParser can emit clear validation errors.
        if (!isForeign) {
            if (parent && parentVariable) {
                return null;
            }
            if (parent && parent !== 'current') {
                return null;
            }
        }
        const selectionTarget = (attrs.selectiontarget || '').trim();
        const selectionVariable = (attrs.selectionvariable || '').trim();
        const contentVariable = (attrs.contentvariable || '').trim();
        const emptyMessage = (attrs.emptymessage || '').trim();
        if (!isForeign) {
            if ((selectionTarget && !selectionVariable) ||
                (!selectionTarget && selectionVariable)) {
                return null;
            }
            if (selectionTarget &&
                (!/^[a-zA-Z0-9_-]+$/.test(selectionTarget) ||
                    !/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(selectionVariable))) {
                return null;
            }
            if (parentVariable && !/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(parentVariable)) {
                return null;
            }
            if (contentVariable && !/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(contentVariable)) {
                return null;
            }
        }
        else {
            if (selectionTarget &&
                selectionVariable &&
                (!/^[a-zA-Z0-9_-]+$/.test(selectionTarget) ||
                    !/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(selectionVariable))) {
                return null;
            }
            if (ownerVariable && !/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(ownerVariable)) {
                return null;
            }
            if (contentVariable && !/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(contentVariable)) {
                return null;
            }
        }
        return {
            type: 'entity-list',
            entityListTypes: attrs.types,
            ...(parent ? { entityListParent: parent } : {}),
            ...(parentVariable ? { entityListParentVariable: parentVariable } : {}),
            entityListSort: sort,
            ...(selectionTarget ? { entityListSelectionTarget: selectionTarget } : {}),
            ...(selectionVariable ? { entityListSelectionVariable: selectionVariable } : {}),
            ...(source ? { entityListSource: source } : {}),
            ...(owner ? { entityListOwner: owner } : {}),
            ...(ownerVariable ? { entityListOwnerVariable: ownerVariable } : {}),
            ...(contentVariable ? { entityListContentVariable: contentVariable } : {}),
            ...(emptyMessage ? { entityListEmptyMessage: emptyMessage } : {}),
            content: ''
        };
    }
    /**
     * Parse a Time-Picker tag into a rich text element
     */
    static parseTimePickerElement(text) {
        const match = text.match(/^<Time-Picker\b([^>]*)\/>$/);
        if (!match) {
            return null;
        }
        const attributesText = (match[1] || '').trim();
        const attributes = this.parseTimePickerAttributes(attributesText);
        if (!attributes) {
            return null;
        }
        return {
            type: 'time-picker',
            timePickerAttributes: attributes,
            content: ''
        };
    }
    /**
     * Parse a Date-Picker tag into a rich text element
     */
    static parseDatePickerElement(text) {
        const match = text.match(/^<Date-Picker\b([^>]*)\/>$/);
        if (!match) {
            return null;
        }
        const attributesText = (match[1] || '').trim();
        const attributes = this.parseDatePickerAttributes(attributesText);
        if (!attributes) {
            return null;
        }
        return {
            type: 'date-picker',
            datePickerAttributes: attributes,
            content: ''
        };
    }
    /**
     * Parse and validate Date-Picker attributes
     */
    static parseDatePickerAttributes(attributesText) {
        if (!attributesText) {
            return null;
        }
        const allowedKeys = new Set(['variable']);
        const attributesMap = this.parseGenericAttributes(attributesText, allowedKeys);
        if (!attributesMap) {
            return null;
        }
        const variable = attributesMap.get('variable');
        if (!variable || !this.isValidVariableIdentifier(variable)) {
            return null;
        }
        return { variable };
    }
    /**
     * Parse and validate Time-Picker attributes
     */
    static parseTimePickerAttributes(attributesText) {
        if (!attributesText) {
            return null;
        }
        const allowedKeys = new Set(['variable']);
        const attributesMap = this.parseGenericAttributes(attributesText, allowedKeys);
        if (!attributesMap) {
            return null;
        }
        const variable = attributesMap.get('variable');
        if (!variable || !this.isValidVariableIdentifier(variable)) {
            return null;
        }
        return { variable };
    }
    /**
     * Parse generic key=value attributes into a Map. Returns null on parse error.
     * Used by Spinner and Time-Picker attribute parsing.
     */
    static parseGenericAttributes(attributesText, allowedKeys) {
        const attributesMap = new Map();
        let index = 0;
        while (index < attributesText.length) {
            while (index < attributesText.length && /\s/.test(attributesText[index])) {
                index++;
            }
            if (index >= attributesText.length) {
                break;
            }
            const keyStart = index;
            while (index < attributesText.length && /[a-zA-Z]/.test(attributesText[index])) {
                index++;
            }
            if (keyStart === index) {
                return null;
            }
            const key = attributesText.slice(keyStart, index);
            if (!allowedKeys.has(key) || attributesMap.has(key)) {
                return null;
            }
            while (index < attributesText.length && /\s/.test(attributesText[index])) {
                index++;
            }
            if (attributesText[index] !== '=') {
                return null;
            }
            index++;
            while (index < attributesText.length && /\s/.test(attributesText[index])) {
                index++;
            }
            if (index >= attributesText.length) {
                return null;
            }
            let value = '';
            const quoteChar = attributesText[index];
            if (quoteChar === '"' || quoteChar === "'") {
                index++;
                const valueStart = index;
                while (index < attributesText.length && attributesText[index] !== quoteChar) {
                    index++;
                }
                if (index >= attributesText.length) {
                    return null;
                }
                value = attributesText.slice(valueStart, index);
                index++;
            }
            else {
                const valueStart = index;
                while (index < attributesText.length && !/\s/.test(attributesText[index])) {
                    index++;
                }
                value = attributesText.slice(valueStart, index);
            }
            attributesMap.set(key, value);
        }
        return attributesMap;
    }
    /**
     * Validate variable identifier format
     */
    static isValidVariableIdentifier(value) {
        return /^[a-zA-Z_][a-zA-Z0-9_]*$/.test(value);
    }
    /**
     * Parse option tag parameters and content using standard rich text parsing
     */
    static parseOptionTag(params, content) {
        // Parse parameters: target=scene_id:effects='''script''':display=block
        let remainingParams = params;
        let target;
        let effects;
        let display;
        // Prefer ":effects=..." so we do not leave a stray ":" after target (target=id:effects=...)
        const effectsColonTriple = remainingParams.match(/:effects='''([\s\S]*?)'''/);
        const effectsBareTriple = remainingParams.match(/effects='''([\s\S]*?)'''/);
        const effectsMatch = effectsColonTriple || effectsBareTriple;
        if (effectsMatch) {
            effects = effectsMatch[1];
            remainingParams = remainingParams.replace(effectsMatch[0], '');
        }
        else {
            const effectsColonQuoted = remainingParams.match(/:effects="([^"]*)"/);
            const effectsQuotedMatch = effectsColonQuoted || remainingParams.match(/effects="([^"]*)"/);
            if (effectsQuotedMatch) {
                effects = effectsQuotedMatch[1];
                remainingParams = remainingParams.replace(effectsQuotedMatch[0], '');
            }
        }
        // Target may contain ':' (e.g. START_OR_RESUME_ADVENTURE:adventure-id); do not use [^:]+
        const tqTargetMatch = remainingParams.match(/target='''([\s\S]*?)'''/);
        if (tqTargetMatch) {
            target = `'''${tqTargetMatch[1]}'''`;
            remainingParams = remainingParams.replace(tqTargetMatch[0], '');
        }
        else {
            const dqTargetMatch = remainingParams.match(/target="([^"]*)"/);
            if (dqTargetMatch) {
                target = dqTargetMatch[1];
                remainingParams = remainingParams.replace(dqTargetMatch[0], '');
            }
            else {
                const sqTargetMatch = remainingParams.match(/target='([^']*)'/);
                if (sqTargetMatch) {
                    target = sqTargetMatch[1];
                    remainingParams = remainingParams.replace(sqTargetMatch[0], '');
                }
                else {
                    const unquotedTargetMatch = remainingParams.match(/target=(.*?)(?=:display=|:conditions=|:effects=|:back=|$)/);
                    if (unquotedTargetMatch) {
                        target = unquotedTargetMatch[1]?.trim();
                        remainingParams = remainingParams.replace(unquotedTargetMatch[0], '');
                    }
                }
            }
        }
        const displayMatch = remainingParams.match(/display=(block|inline)/);
        if (displayMatch) {
            display = displayMatch[1];
        }
        let condition;
        const conditionsColonMatch = remainingParams.match(/:conditions='''([\s\S]*?)'''/);
        const conditionsBareMatch = remainingParams.match(/conditions='''([\s\S]*?)'''/);
        const conditionsMatch = conditionsColonMatch || conditionsBareMatch;
        if (conditionsMatch) {
            condition = conditionsMatch[1];
        }
        let isBackOption = false;
        const backColonMatch = params.match(/:back=(true|false|1|0)/i);
        const backBareMatch = params.match(/(?:^|:)back=(true|false|1|0)/i);
        const backMatch = backColonMatch || backBareMatch;
        if (backMatch) {
            const backValue = backMatch[1].toLowerCase();
            isBackOption = backValue === 'true' || backValue === '1';
        }
        const richTextDescription = this.parseRichTextDescription(content);
        return {
            type: 'inline-option',
            target: target,
            effects: effects,
            display: display,
            condition: condition,
            ...(isBackOption ? { isBackOption: true } : {}),
            content: richTextDescription.elements
        };
    }
}
exports.RichTextParser = RichTextParser;
//# sourceMappingURL=RichTextParser.js.map